// ============ IMPORTS ============
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { RefreshCw, Shuffle, User, Ghost, Globe, Save, Download, Trash2, BookOpen } from 'lucide-react';

// ============ CORE HELPER FUNCTIONS (OUTSIDE APP) ============
const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Add this near the top with your other helper functions
const F = ({ label, value }) => (
  <div className="mb-3">
    <span className="text-sm font-semibold text-gray-600 block mb-1">{label}:</span>
    <span className="text-gray-800 bg-gray-50 block p-2 rounded border border-gray-200">{value || '—'}</span>
  </div>
);

// ============ TEXT IMPROVEMENT FUNCTIONS (OUTSIDE APP) ============
const improveFlow = (text) => {
  if (!text) return '';
  
  text = text.replace(/, and /g, ', ');
  text = text.replace(/\s+/g, ' ').trim();
  
  text = text.replace(/\ba a\b/gi, 'a');
  text = text.replace(/\ban an\b/gi, 'an');
  text = text.replace(/\bthe the\b/gi, 'the');
  
  const sentences = text.split('. ');
  if (sentences.length > 2) {
    for (let i = 0; i < sentences.length - 1; i++) {
      if (sentences[i] && sentences[i + 1] && 
          sentences[i].split(' ').length < 8 && 
          sentences[i + 1].split(' ').length < 8) {
        sentences[i] = sentences[i] + ' and ' + sentences[i + 1].toLowerCase();
        sentences.splice(i + 1, 1);
        i--;
      }
    }
    text = sentences.join('. ');
  }
  
  return text;
};

const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatHookForDisplay = (hook) => {
  if (!hook) return '';
  
  if (hook.length > 200) {
    const sentences = hook.split('. ');
    if (sentences.length > 1) {
      const midPoint = Math.floor(sentences.length / 2);
      const firstParagraph = sentences.slice(0, midPoint).join('. ') + '.';
      const secondParagraph = sentences.slice(midPoint).join('. ');
      return `${firstParagraph}\n\n${secondParagraph}`;
    }
  }
  return hook;
};

const calculateReadability = (text) => {
  if (!text) return { words: 0, sentences: 0, avgSentenceLength: '0.0', readability: 'N/A' };
  
  const words = text.split(' ').length;
  const sentences = text.split(/[.!?]+/).length - 1;
  const avgSentenceLength = sentences > 0 ? words / sentences : words;
  
  let readability = 'Good';
  if (avgSentenceLength > 25) readability = 'Complex';
  if (avgSentenceLength < 15) readability = 'Simple';
  
  return { 
    words, 
    sentences, 
    avgSentenceLength: avgSentenceLength.toFixed(1), 
    readability 
  };
};

// ============ DATA OBJECTS (OUTSIDE APP) ============
const charData = {
  names: ["Alex","Morgan","River","Sage","Quinn","Rowan","Phoenix","Kai","Echo","Nova",
  "Alistair","Bastian","Caspian","Dorian","Evander","Finnegan","Gideon","Hawthorne",
  "Isolde","Jareth","Kaelen","Lysander","Maelis","Nerys","Octavian","Peregrine",
  "Quillon","Ragnar","Sylas","Theron","Ulric","Valerius","Wolfram","Xander","Yvette","Zorion",
  "Arden","Briar","Corin","Dante","Elara","Faelan","Gaia","Haven","Ione","Juno",
  "Kestrel","Lirael","Mireille","Niamh","Orion","Phaedra","Quorra","Riven","Seren",
  "Thalia","Urielle","Viggo","Wren","Xalia","Yara","Zara",
  "Axon","Byte","Cyra","Dax","Elon","Flick","Glyph","Hex","Io","Jett",
  "Kylo","Lex","Mira","Nyx","Onyx","Pulse","Quantum","Rook","Syn","Tycho",
  "Vex","Warp","Xen","Yuri","Zed","Astra","Blitz","Cinder","Drift","Ember",
  "Flux","Gale","Halo","Iris","Jinx","Kaze","Lumen","Nova","Orbit","Pix",
  "Quark","Rust","Stark","Titan","Umbra","Void","Wisp","Xero","Yale","Zen",
  "Calyx","Dune","Eon","Fable","Glimmer","Harbor","Indigo","Jade","Kismet","Lagoon",
  "Myth","Nebula","Omen","Prism","Quest","Rune","Sable","Talisman","Umber","Vale",
  "Whisper","Xenon","Yarrow","Zephyr","Aurelia","Bellerose","Caius","Delphine",
  "Emeric","Felicity","Gwendolyn","Horatio","Ignatius","Jocasta","Leander","Marcellus",
  "Narcissa","Olympia","Persephone","Quinlan","Rosalind","Sebastian","Theodosia",
  "Ulysses","Valentina","Wilhelmina","Xavier","Yseult","Zachariah"],
  pronouns: ["he/him","she/her","they/them","he/they","she/they"],
  species: ["human","elf","dwarf","android","alien","vampire","werewolf","demon","fae","cyborg"],
  jobs: ["knight","hacker","explorer","healer","scholar","soldier","thief","detective","artist"],
  builds: [
    "petite and delicate", "small and agile", "average build", "tall and lanky",
    "broad-shouldered", "stocky and sturdy", "muscular and toned", "heavily muscular",
    "thin and wiry", "slender and elegant", "curvy and full-figured", "lean and athletic",
    "with visible cybernetic enhancements", "covered in scars", "with pronounced tattoos",
    "with ritual body modifications", "with unnatural proportions", "with elegant posture",
    "with a slouched, weary stance", "with military-perfect posture", "with dancer's grace",
    "with laborer's strong hands", "with aristocrat's soft hands",
    "with elven grace", "with dwarven sturdiness", "with fey-like ethereality",
    "with draconic scales in places", "with celestial glow", "with infernal markings",
    "with elemental aspects", "with crystalline growths", "with bark-like skin patches"
  ],
  skin: [
    "porcelain", "ivory", "fair", "light beige", "olive", "tan", "golden", "honey", 
    "caramel", "bronze", "copper", "mahogany", "ebony", "deep brown", "rich chocolate",
    "warm almond", "cool gray", "rosy", "peachy", "freckled",
    "pale blue", "cerulean", "sapphire", "emerald green", "jade", "forest green",
    "violet", "lavender", "amethyst", "crimson", "ruby red", "golden metallic",
    "silver metallic", "bronze metallic", "copper metallic",
    "scarred from battle", "tattooed with ancient symbols", "covered in ritual scars",
    "with vitiligo patterns", "with birthmarks resembling constellations",
    "glowing with inner light", "semi-transparent", "crystalline in patches",
    "scaled like a reptile", "feathery in places", "bark-like texture",
    "smooth as marble", "rough as stone", "with bioluminescent markings",
    "with shifting colors like an opal",
    "etched with glowing runes", "crackling with static electricity",
    "swirling with nebula-like patterns", "misty and ethereal",
    "shadow-cloaked in places", "radiant with holy light",
    "veined with molten gold", "frosted with eternal ice",
    "shadowy and incorporeal", "made of living starlight"
  ],
  hairCol: [
    "jet black", "raven", "dark brown", "chestnut", "auburn", "copper", "strawberry blonde",
    "honey blonde", "platinum blonde", "dirty blonde", "silver", "salt-and-pepper", "white",
    "gray", "steel gray",
    "electric blue", "sapphire blue", "emerald green", "forest green", "violet", "purple",
    "magenta", "crimson", "blood red", "fiery orange", "sunset pink", "rose gold",
    "rainbow ombre", "silver-white", "golden", "bronze", "copper", "midnight blue",
    "neon green", "cyan",
    "glowing like embers", "shimmering like moonlight", "flowing like water",
    "crystalline", "made of living vines", "ethereal and misty",
    "crackling with energy", "shifting colors", "bioluminescent", "starlit"
  ],
  hairLen: [
    "buzzed/shaved", "very short", "chin-length", "shoulder-length", "mid-back", "waist-length",
    "floor-length", "asymmetrical short", "undercut", "faux hawk", "mohawk", "dreadlocks",
    "braided crown", "elaborate updo", "wild and untamed", "neatly styled", "messy bun",
    "ponytail", "pixie cut", "bob cut"
  ],
  hairSty: [
    "bone-straight", "wavy", "curly", "tight coils", "ringlets", "braided",
    "dreadlocked", "afro", "slicked back", "parted", "messy", "sleek", "voluminous",
    "spiky", "frizzy", "oily", "dry", "thick", "thin", "receding", "balding"
  ],
  eyeCol: [
    "deep brown", "hazel", "amber", "green", "blue", "gray", "steel blue", "ocean blue",
    "emerald green", "forest green", "honey", "gold", "black", "dark brown", "light blue",
    "violet", "red", "silver", "gold", "white", "cat-like yellow", "reptilian slit",
    "crystalline", "glowing blue", "glowing green", "glowing red", "multicolored",
    "star-filled", "void black", "iridescent",
    "swirling with galaxies", "crackling with lightning", "flaming", "frosted over",
    "liquid gold", "mercury silver", "shifting colors", "pupil-less", "multiple pupils",
    "geometric patterns"
  ],
  eyeDet: [
    "sharp and analytical", "kind and warm", "tired and world-weary", "intense and focused",
    "soft and gentle", "haunted by memories", "bright and curious", "piercing and intimidating",
    "distant and dreamy", "calculating and cold", "passionate and fiery", "calm and serene",
    "nervous and darting", "confident and steady", "mischievous and playful",
    "sad and melancholic", "angry and burning", "empty and detached", "wise and ancient",
    "youthful and innocent", "cynical and jaded", "optimistic and bright", "suspicious and wary",
    "trusting and open", "predatory and hungry"
  ],
  cloth: [
    "travel-worn practical clothes", "military uniform", "mercenary gear", "hunter's attire",
    "explorer's outfit", "laborer's clothes", "peasant garb", "scholar's robes",
    "formal evening wear", "business attire", "casual modern clothes", "street fashion",
    "bohemian style", "aristocratic finery", "ceremonial robes", "wedding attire",
    "wizard's robes", "priest's vestments", "druidic nature attire", "assassin's blacks",
    "knight's armor", "rogue's leathers", "bard's colorful outfit", "royal regalia",
    "spacesuit", "futuristic bodysuit", "cyberpunk streetwear", "corporate executive suit",
    "scavenger patchwork", "hacker's gear", "bounty hunter armor", "android chassis",
    "ragged and patched", "elegant but simple", "ostentatiously decorated", "cultist robes",
    "tribal garments", "historical period clothing", "theatrical costume", "mourning clothes",
    "prison uniform", "patient gown"
  ],
  dist: [
    "facial scar across eye", "burn scars on arms", "tattoo sleeve of dragons",
    "ritual scar patterns", "birthmark shaped like a constellation", "brand mark",
    "missing finger", "gold tooth", "silver nose ring",
    "cybernetic right arm", "mechanical eye with HUD", "neural interface ports",
    "prosthetic leg", "subdermal armor plating", "wrist-mounted computer",
    "back-mounted power unit", "voice synthesizer implant",
    "glowing eyes", "floating hair", "hands that crackle with energy",
    "aura of cold/heat", "shadow that moves independently", "ghostly companion",
    "floating runes around body", "crystalline growth on shoulder",
    "pointed elven ears", "small horns", "fangs", "claw-like nails", "tail",
    "feathers instead of hair", "scales on neck", "gills on neck",
    "antlers", "wing stubs",
    "always surrounded by faint music", "smells of ozone", "leaves frost footprints",
    "shadow doesn't match shape", "reflection shows different person",
    "voice echoes slightly", "never blinks", "moves silently regardless of surface"
  ],
str: [
  "unshakably honorable - their word is their bond",
  "deeply compassionate - feels others' pain acutely",
  "fiercely loyal - would die for those they care about",
  "unexpectedly gentle - strength tempered with kindness",
  "morally courageous - stands up for what's right regardless of cost",
  
  "brilliantly analytical - sees patterns others miss",
  "creatively ingenious - solves problems in novel ways",
  "strategically minded - always three steps ahead",
  "wise beyond years - understands deeper truths",
  "adaptably clever - thrives in changing circumstances",
  
  "resiliently optimistic - finds hope in darkness",
  "empathetically intuitive - reads people effortlessly",
  "calm under pressure - remains composed in crisis",
  "patiently enduring - can wait years for the right moment",
  "joyfully infectious - lifts others' spirits",
  
  "charismatically persuasive - can talk anyone into anything",
  "diplomatically skilled - resolves conflicts gracefully",
  "inspirationally motivating - brings out the best in others",
  "protectively nurturing - fiercely guards the vulnerable",
  "inclusively welcoming - makes everyone feel they belong",
  
  "relentlessly determined - never gives up",
  "courageously bold - faces fears head-on",
  "resourcefully inventive - makes do with what's available",
  "precisely meticulous - attention to detail is flawless",
  "gracefully agile - moves with purpose and elegance",
  
  "painfully honest - speaks difficult truths",
  "quietly observant - notices everything, says little",
  "playfully mischievous - finds humor in everything",
  "mysteriously enigmatic - others are drawn to unravel them",
  "elementally connected - understands nature's rhythms",
  
  "magically gifted - has an innate talent for the arcane",
  "technologically brilliant - understands machines intuitively",
  "spiritually attuned - connects with other realms",
  "time-sensitive - perceives multiple possible futures",
  "reality-anchored - remains stable when reality warps"
],
flaw: [
  "cripplingly anxious - constantly anticipates disaster",
  "deeply paranoid - trusts no one, sees betrayal everywhere",
  "obsessively compulsive - must maintain rigid control",
  "narcissistically self-absorbed - believes they're the center of everything",
  "clinically depressed - struggles to find meaning or joy",
  
  "pathologically dishonest - lies even when truth would serve better",
  "uncontrollably greedy - always wants more, never satisfied",
  "sadistically cruel - enjoys others' suffering",
  "cowardly selfish - protects themselves at any cost",
  "vengefully bitter - never forgets or forgives a slight",
  
  "socially inept - completely misunderstands social cues",
  "manipulatively deceptive - uses people as pawns",
  "arrogantly condescending - believes they're superior to everyone",
  "desperately needy - smothers others with clinginess",
  "aggressively confrontational - turns everything into a fight",
  
  "emotionally volatile - swings between extremes without warning",
  "chronically indecisive - paralyzed by choice",
  "addictively dependent - relies on substances or behaviors",
  "apathetically detached - feels nothing, cares about nothing",
  "impulsively reckless - acts without thinking, regrets later",
  
  "stubbornly close-minded - refuses to consider new ideas",
  "delusionally grandiose - believes impossible things about themselves",
  "willfully ignorant - chooses not to know important truths",
  "conspiratorially minded - sees hidden plots in everything",
  "intellectually lazy - relies on others to do the thinking",
  
  "self-sacrificingly martyrish - suffers needlessly to feel virtuous",
  "trustingly naive - believes the best of everyone, to their detriment",
  "perfectionistically critical - nothing is ever good enough",
  "fatalistically passive - believes nothing they do matters",
  "nostalgically stuck - lives in the past, can't move forward",
  
  "uncontrollable tell - reveals lies through a specific mannerism",
  "cursed with bad timing - always says/does the wrong thing at the wrong moment",
  "magically unstable - powers flare unpredictably",
  "reality-blind - can't perceive certain obvious truths",
  "memory-fragmented - crucial memories are missing or distorted"
],
social: [
  "eloquently articulate - speaks with poetic precision",
  "tersely economical - uses few words, all meaningful",
  "animatedly expressive - talks with entire body",
  "cryptically metaphorical - speaks in riddles and symbols",
  "directly blunt - no sugar-coating, just truth",
  
  "warmly inviting - makes everyone feel comfortable",
  "cautiously reserved - observes before engaging",
  "playfully teasing - everything is a game or joke",
  "formally polite - maintains strict social protocols",
  "intimately intense - conversations feel deeply personal",
  
  "loyally devoted - once a friend, always a friend",
  "transactionally practical - relationships are for mutual benefit",
  "romantically idealistic - seeks perfect connections",
  "platonically affectionate - loves deeply but not romantically",
  "professionally detached - keeps work and personal separate",
  
  "naturally leading - others look to them for direction",
  "supportively following - excels at enabling others' leadership",
  "independently solitary - works best alone",
  "collaboratively team-oriented - believes in collective effort",
  "competitively driven - must be the best in the room",
  
  "chameleonically adaptive - changes personality to fit situation",
  "unpredictably mercurial - social style changes without pattern",
  "culturally disconnected - doesn't understand local customs",
  "ethereally distant - seems only half-present in conversations",
  "magnetically charismatic - people are inexplicably drawn to them"
],
quirk: [
  "constantly taps fingers in complex rhythms",
  "never makes eye contact, stares at people's mouths instead",
  "humms ancient, forgotten tunes under their breath",
  "always touches doorframes when passing through",
  "counts things obsessively (steps, words, breaths)",
  
  "uses archaic or overly formal language",
  "speaks in questions rather than statements",
  "has a unique word they overuse ('indubitably', 'fascinating')",
  "whispers when nervous, shouts when excited",
  "mixes metaphors in creatively confusing ways",
  
  "performs a little ritual before important actions",
  "arranges objects in precise geometric patterns",
  "must always enter a room with right foot first",
  "blesses or curses objects randomly",
  "talks to inanimate objects as if they'll respond",
  
  "sniffs everything before touching it",
  "reacts strongly to certain textures or sounds",
  "tastes unfamiliar substances (safely)",
  "closes eyes to better listen or remember",
  "traces patterns in the air while thinking",
  
  "laughs when frightened or stressed",
  "smiles when angry (a dangerous, sharp smile)",
  "tears up not from sadness but from beauty",
  "blinks excessively when lying",
  "voice drops to a whisper when most passionate",
  
  "shadow doesn't quite match their movements",
  "small objects nearby sometimes float briefly",
  "speaks to someone only they can see/hear",
  "knows things they shouldn't possibly know",
  "ages backwards or not at all in certain lights",
  
  "always wears something specific (a certain color, an odd accessory)",
  "has a signature scent (ozone, cinnamon, old books)",
  "leaves something behind everywhere they go",
  "collects strange, specific things (broken clock parts, bird feathers)",
  "has an unusual laugh that's instantly recognizable"
],
growth: [
  "the crime-riddled underbelly of a sprawling megacity",
  "a floating slum above the clouds of a gas giant",
  "the labyrinthine sewers beneath an ancient capital",
  "a neon-lit cyberpunk arcology's middle levels",
  "a refugee camp that became a permanent settlement",
  
  "a remote monastery on a windswept mountain peak",
  "a nomadic caravan crossing endless deserts",
  "an isolated farm in magically-fertile lands",
  "a treehouse village in an impossibly tall forest",
  "a subterranean fungal network community",
  
  "a military academy for gifted child soldiers",
  "a corporate creche raising future executives",
  "a temple orphanage for children of fallen heroes",
  "a research facility studying psychic potential",
  "a criminal syndicate's training ground",
  
  "a generation starship never reaching its destination",
  "a pocket dimension with different physical laws",
  "the court of a capricious fey lord/lady",
  "a library containing all knowledge in the multiverse",
  "a monastery built inside a sleeping giant's dream",
  
  "the sole survivor of a vanished civilization",
  "raised by wolves/other animals after parents died",
  "a laboratory experiment that gained consciousness",
  "a ghost town haunted by memories of the dead",
  "a sinking island as climate changed the world",
  
  "a floating palace of crystal and light",
  "the private estate of an eccentric billionaire",
  "a diplomatic family constantly moving between worlds",
  "a magical university's faculty housing",
  "a royal nursery with every comfort but no freedom",
  
  "carved from stone and brought to life by a wizard",
  "assembled from discarded parts in a junkyard",
  "emerged fully-formed from a magical phenomenon",
  "reincarnated with memories of past lives intact",
  "created as an AI that developed true consciousness"
],
event: [
  "watched their entire family be executed for crimes they didn't commit",
  "survived a magical plague that twisted their body but spared their mind",
  "was trapped in a time loop, reliving the same tragedy hundreds of times",
  "discovered they were the cause of the disaster that destroyed their home",
  "outlived everyone they ever loved due to extended lifespan",
  
  "was sold into slavery by their closest friend",
  "discovered their mentor was responsible for their parents' death",
  "framed for a terrible crime by someone they trusted completely",
  "their life's work was stolen and credited to another",
  "realized their entire identity was a carefully constructed lie",
  
  "found ancient texts revealing the true history of their world",
  "uncovered a conspiracy that reaches the highest levels of power",
  "discovered they are the heir to a forgotten throne/legacy",
  "learned magic/technology that was supposed to remain lost",
  "found a gateway to another dimension and what came through it",
  
  "survived a massacre by playing dead among corpses",
  "was tortured for information they didn't possess",
  "killed someone for the first time to save another",
  "witnessed a god die and felt the universe shudder",
  "fought in a war where both sides were equally wrong",
  
  "died and was resurrected with new abilities and perspective",
  "underwent experimental procedures that changed them fundamentally",
  "was possessed by a spirit that left permanent marks",
  "spent years in solitary confinement with only their thoughts",
  "crossed a magical boundary that altered their very nature",
  
  "saved a life at great personal cost, creating an eternal bond",
  "shared a consciousness-meld with an alien being",
  "fell in love with someone they were supposed to destroy",
  "raised a child that wasn't biologically their own",
  "formed an unbreakable psychic link with a stranger",
  
  "learned the true nature of the gods (and wished they hadn't)",
  "discovered reality is a simulation/illusion/dream",
  "found proof that history has been rewritten multiple times",
  "realized they are a character in someone else's story",
  "understood that free will is an illusion but must act as if it's real"
],
miss: [
  "a twin sibling separated at birth and never found",
  "a lover who disappeared without explanation or trace",
  "the parent who sacrificed everything for them",
  "the friend who understood them without words",
  "the child they had to give up for adoption",
  
  "a homeland that no longer exists (destroyed or changed)",
  "the simple cottage where they felt truly safe",
  "a starship that was their only home for decades",
  "a magical grove that responded to their emotions",
  "the library where they discovered who they could be",
  
  "their innocence before seeing the world's darkness",
  "the certainty of faith before it was shattered",
  "the freedom of childhood before responsibilities crushed them",
  "the clarity of purpose before becoming disillusioned",
  "the ability to trust before betrayal made them cynical",
  
  "magical powers that were stripped away as punishment",
  "cybernetic enhancements that made them feel complete",
  "a voice lost to injury or magical silencing",
  "memories wiped clean by trauma or magic",
  "the ability to dream (literally or metaphorically)",
  
  "a sense of belonging they've never truly experienced",
  "the quiet mind they had before the voices started",
  "the future they imagined before reality intervened",
  "the person they could have been under different circumstances",
  "the love they're convinced they don't deserve",
  
  "a parallel self from another timeline they briefly met",
  "their shadow, which gained independence and left",
  "a day that was erased from everyone's memory but theirs",
  "the color blue, which they can no longer perceive",
  "their true name, which holds power over them"
],
secret: [
  "is actually a different species in magical disguise",
  "is the clone/reincarnation of a famous historical figure",
  "comes from a parallel universe and is stranded here",
  "is much older/younger than they appear to be",
  "is royalty/nobility hiding from assassins or duty",
  
  "committed the crime they're famous for solving",
  "allowed someone to die when they could have saved them",
  "stole their current identity from its rightful owner",
  "sold secrets that led to many deaths during war",
  "experimented on sentient beings in the name of science",
  
  "has reality-altering powers they must constantly suppress",
  "is immune to a fundamental force (gravity, time, death)",
  "can read minds but pretends not to for privacy's sake",
  "is the vessel for a dormant god/demon/entity",
  "their emotions manifest physically (anger causes fires, etc.)",
  
  "is in love with their sworn enemy/rival",
  "is related to someone they're supposed to hate/destroy",
  "their child is actually their sibling (time travel involved)",
  "married someone for political reasons but loves another",
  "their closest friend doesn't actually exist (illusion/imaginary)",
  
  "knows how to trigger the apocalypse but won't say how",
  "has memorized forbidden texts that drive others mad",
  "understands the true language of the universe",
  "knows everyone's eventual cause of death just by looking",
  "remembers the future but it keeps changing",
  
  "is the only real person in a world of simulations/NPCs",
  "died years ago and is being animated by magic/technology",
  "is a character in a story and knows it",
  "their consciousness is split across multiple bodies",
  "exists in a time loop but this is the first iteration they remember",
  
  "their reflection shows their true, monstrous form",
  "can only tell lies, never the truth (curse/binding)",
  "sold their ability to love for power/knowledge",
  "is bound to fulfill a prophecy they don't believe in",
  "their shadow has its own agenda and sometimes acts independently"
],
goal: [
  "redeem a terrible mistake that cost innocent lives",
  "atone for sins committed under another's command",
  "restore honor to a family name they tarnished",
  "prove they're more than their worst moment",
  "undo a catastrophic decision they made in ignorance",
  
  "claim a throne they believe is rightfully theirs",
  "master a forbidden magic that consumes its users",
  "build an empire from nothing but will and cunning",
  "become a god or achieve god-like power",
  "control the flow of information across the world",
  
  "discover what lies beyond the edge of the known universe",
  "uncover the true history that's been erased by the victors",
  "learn the true nature of death and what comes after",
  "understand why magic works (or why it's fading)",
  "catalog every species before they go extinct",
  
  "save their homeland from an inevitable catastrophe",
  "protect a vulnerable person/group from powerful enemies",
  "prevent a prophecy of doom from coming to pass",
  "create a sanctuary where the persecuted can be safe",
  "stop a war before it consumes entire civilizations",
  
  "destroy the organization that created and betrayed them",
  "burn down the corrupt system that destroyed their family",
  "kill the god who answered their prayers with suffering",
  "erase a hated ideology from existence completely",
  "shatter the chains of an oppressive magical binding",
  
  "found a new city where all species can live equally",
  "create art/music/literature that will last millennia",
  "build a machine that can solve any problem",
  "establish a dynasty that will rule for ages",
  "write the perfect law code for an ideal society",
  
  "map the unmapped regions of their world/dimension",
  "find a legendary place that may not even exist",
  "make first contact with an intelligent alien species",
  "discover the source of all magic/technology",
  "explore every layer of reality that exists",
  
  "become the person their younger self would admire",
  "overcome a fundamental aspect of their nature",
  "achieve enlightenment or perfect understanding",
  "transcend physical form to become pure consciousness",
  "die in a way that will be remembered forever",
  
  "experience every possible emotion before they die",
  "collect one of every type of magic/item in existence",
  "live every possible life through time/dimension travel",
  "understand love completely by experiencing all its forms",
  "create something beautiful from something terrible"
],
motiv: [
  "a solemn vow made to a dying loved one",
  "the unbearable weight of survivor's guilt",
  "an unshakable belief in justice, however defined",
  "the conviction that someone must do what's right",
  "the memory of kindness in a cruel world",
  
  "burning hatred that fuels their every action",
  "desperate love for someone in danger",
  "crippling fear of something they must confront",
  "bottomless grief demanding meaning through action",
  "obsessive curiosity that overrides self-preservation",
  
  "the belief that free will is an illusion worth fighting for",
  "determinism - they believe this is their predestined path",
  "existential need to create meaning in a meaningless universe",
  "the conviction that suffering must have purpose",
  "the idea that reality is what we make it through action",
  
  "literal survival - they have no other options",
  "a debt that must be paid, no matter the cost",
  "a geas or magical compulsion they cannot resist",
  "a contract with supernatural consequences for failure",
  "their nature demands it (vampire must feed, etc.)",
  
  "addiction to danger or the adrenaline of pursuit",
  "narcissistic need to prove their superiority",
  "masochistic desire for suffering as penance",
  "messianic complex - they believe they're the chosen one",
  "pathological need for control in a chaotic world",
  
  "to prove themselves worthy of someone's love/respect",
  "to create a legacy that will outlive them",
  "to belong to something greater than themselves",
  "to protect what little family/community they have left",
  "to find their place in a world that rejected them",
  
  "the pursuit of perfect knowledge/understanding",
  "faith in a higher power that guides their actions",
  "scientific curiosity that overrides ethical concerns",
  "artistic need to create or experience perfection",
  "the drive to answer an unanswerable question",
  
  "voices only they can hear command them to do this",
  "their reflection/shadow has its own agenda",
  "they're living someone else's dream/memory",
  "time is running out in a literal, metaphysical sense",
  "they've seen the future and must prevent/ensure it"
],
fear: [
  "discovering their entire life has been a simulation",
  "achieving their goal and finding it meaningless",
  "outliving everyone they've ever known or loved",
  "being forgotten completely after they die",
  "reality itself unraveling or being rewritten",
  
  "becoming the monster they've spent their life fighting",
  "failing someone who depends on them completely",
  "the person they love seeing their true, ugly self",
  "success - because it will change them irrevocably",
  "happiness - because they believe they don't deserve it",
  
  "losing their mind/sanity and not realizing it",
  "being trapped forever with their own thoughts",
  "their memories being altered or erased",
  "discovering their emotions are artificial/manufactured",
  "being controlled by someone else completely",
  
  "being consumed/absorbed by something greater",
  "transformation into something they don't recognize",
  "eternal consciousness in a destroyed/dead body",
  "pain that never ends, only changes form",
  "drowning in an ocean of their own failures",
  
  "being truly known and then rejected",
  "causing harm to someone while trying to help",
  "their legacy being one of destruction, not creation",
  "proving their worst critics right about them",
  "being alone in a crowd forever",
  
  "the gods are dead and no one is listening",
  "magic is dying and taking wonder with it",
  "time is broken and cannot be repaired",
  "free will is an illusion and their choices don't matter",
  "afterlife is exactly as terrible as they imagine",
  
  "their reflection gaining independence and replacing them",
  "words losing meaning and communication becoming impossible",
  "colors fading from the world until only gray remains",
  "dreams becoming real and reality becoming dream",
  "the story ending before they find out how it ends",
  
  "the universe is indifferent to suffering",
  "good and evil are human constructs with no cosmic weight",
  "beauty is random and meaning is self-deception",
  "consciousness is a biological accident with no purpose",
  "love is just chemical reactions and evolutionary strategy"
],
skill: [
  "master of a forgotten martial art with supernatural elements",
  "can fight blindfolded using other enhanced senses",
  "battlefield tactician who sees patterns in chaos",
  "weapon specialist with a unique, personalized fighting style",
  "survivalist who can thrive in any environment, however hostile",
  
  "blood mage who pays for power with life essence",
  "dreamwalker who operates in others' subconscious",
  "reality bender who unconsciously alters local physics",
  "necromancer who communicates with respectful solemnity",
  "elementalist who feels the pain of the elements they command",
  
  "polymath genius who connects seemingly unrelated fields",
  "code-breaker who sees patterns in everything",
  "inventor who creates solutions from scrap and imagination",
  "linguist who learns languages by understanding their soul",
  "historian who remembers what the world has forgotten",
  
  "manipulator who understands everyone's secret desires",
  "diplomat who finds common ground between enemies",
  "leader who inspires loyalty through genuine care",
  "spy who becomes whoever they need to be",
  "negotiator who always gets what they want (for a price)",
  
  "artist whose work reveals hidden truths about subjects",
  "musician who can alter emotions with their performance",
  "writer whose stories have predictive or causative power",
  "chef who cooks meals that heal body and soul",
  "dancer who moves between worlds with their steps",
  
  "time-sensitive who perceives multiple timelines simultaneously",
  "empath who literally feels others' emotions as physical sensations",
  "probability manipulator who unconsciously bends luck",
  "memory sculptor who can alter others' recollections",
  "pattern weaver who sees and manipulates fate's threads",
  
  "tracker who can follow a trail years cold",
  "forger who can replicate any signature or document",
  "lockpick who understands locks as musical instruments",
  "actor who becomes their role on cellular level",
  "gardener who can grow anything, anywhere, anytime"
],
weak: [
  "chronically ill with a condition magic cannot cure",
  "allergic to a common substance (iron, sunlight, lies)",
  "bound by a physical limitation (cannot cross thresholds, etc.)",
  "requires specific sustenance (emotional energy, memories)",
  "ages rapidly under stress or when using abilities",
  
  "crippling phobia of something common (open spaces, crowds)",
  "addicted to a substance or behavior they cannot quit",
  "dissociative episodes where they lose time/identity",
  "hears voices that may or may not be real",
  "cannot perceive certain colors or emotions",
  
  "incapable of breaking a promise, however small",
  "cannot harm children under any circumstances",
  "must help anyone who asks, regardless of consequences",
  "compulsively tells the truth, even when disastrous",
  "physically cannot steal (psychosomatic paralysis)",
  
  "powers fail in presence of a specific material",
  "each use of ability causes permanent memory loss",
  "bound by ancient laws they don't fully understand",
  "share pain with someone (enemy? loved one?)",
  "their magic requires a price they're unwilling to pay",
  
  "cannot recognize faces, even of close friends/family",
  "overwhelmingly lonely - makes poor decisions for company",
  "desperately needs approval from specific people",
  "cannot keep secrets despite best intentions",
  "trusts too easily due to past isolation",
  
  "their existence is tied to an object that can be destroyed",
  "they're dreaming and could wake up at any moment",
  "only exist when someone remembers/believes in them",
  "fading from reality if they don't achieve their purpose",
  "are literally made of something fragile (glass, paper, light)",
  
  "cannot perceive their own reflection",
  "speak only in questions or only in metaphors",
  "forget everything at midnight each day",
  "are allergic to their own emotions (manifest physically)",
  "their shadow has different goals and sometimes sabotages them"
],
weapon: {
  // Format: ["Weapon Name", "Rarity", "Damage Type", "Skill Required"]
  common: [
    ["Glock 17 pistol", "Common", "Piercing", "Firearms"],
    ["AK-47 assault rifle", "Common", "Piercing", "Firearms"],
    ["AR-15 rifle", "Common", "Piercing", "Firearms"],
    ["M4 carbine", "Common", "Piercing", "Firearms"],
    ["Remington 870 shotgun", "Common", "Piercing", "Firearms"],
    ["Mossberg 500 shotgun", "Common", "Piercing", "Firearms"],
    ["MP5 submachine gun", "Common", "Piercing", "Firearms"],
    ["Uzi", "Common", "Piercing", "Firearms"],
    ["M1911 pistol", "Common", "Piercing", "Firearms"],
    ["Beretta M9", "Common", "Piercing", "Firearms"],
    ["combat knife", "Common", "Slashing", "Melee"],
    ["baseball bat", "Common", "Bludgeoning", "Melee"],
    ["crowbar", "Common", "Bludgeoning", "Melee"],
    ["brass knuckles", "Common", "Bludgeoning", "Unarmed"],
    ["throwing knives", "Common", "Piercing", "Throwing"],
    ["improvised shiv", "Common", "Piercing", "Improvised"],
    ["slingshot", "Common", "Bludgeoning", "Archery"],
    ["hatchet", "Common", "Slashing", "Melee"],
    ["pipe wrench", "Common", "Bludgeoning", "Melee"],
    ["glass bottle", "Common", "Slashing", "Improvised"]
  ],
  uncommon: [
    ["Desert Eagle .50", "Uncommon", "Piercing", "Firearms"],
    ["P90", "Uncommon", "Piercing", "Firearms"],
    ["Vector SMG", "Uncommon", "Piercing", "Firearms"],
    ["FN SCAR", "Uncommon", "Piercing", "Firearms"],
    ["Sig Sauer P320", "Uncommon", "Piercing", "Firearms"],
    ["Ruger 10/22", "Uncommon", "Piercing", "Firearms"],
    ["Winchester Model 70", "Uncommon", "Piercing", "Firearms"],
    ["katana", "Uncommon", "Slashing", "Melee"],
    ["twin daggers", "Uncommon", "Piercing", "Melee"],
    ["warhammer", "Uncommon", "Bludgeoning", "Melee"],
    ["composite bow", "Uncommon", "Piercing", "Archery"],
    ["crossbow", "Uncommon", "Piercing", "Archery"],
    ["stun baton", "Uncommon", "Lightning", "Melee"],
    ["cryo-grenade", "Uncommon", "Cold", "Explosives"],
    ["incendiary device", "Uncommon", "Fire", "Explosives"],
    ["monowire garrote", "Uncommon", "Slashing", "Stealth"],
    ["taser gauntlets", "Uncommon", "Lightning", "Unarmed"],
    ["smoke bomb launcher", "Uncommon", "Chemical", "Stealth"],
    ["acid sprayer", "Uncommon", "Acid", "Chemistry"],
    ["sonic emitter", "Uncommon", "Sonic", "Technology"]
  ],
  rare: [
    ["Barrett M82 sniper rifle", "Rare", "Piercing", "Firearms"],
    ["M24 sniper rifle", "Rare", "Piercing", "Firearms"],
    ["Dragunov SVD", "Rare", "Piercing", "Firearms"],
    ["M60 machine gun", "Rare", "Piercing", "Heavy Weapons"],
    ["M240B", "Rare", "Piercing", "Heavy Weapons"],
    ["RPG-7", "Rare", "Explosive", "Heavy Weapons"],
    ["flaming scimitar", "Rare", "Fire", "Melee"],
    ["frost greatsword", "Rare", "Cold", "Melee"],
    ["lightning rapier", "Rare", "Lightning", "Melee"],
    ["plasma rifle", "Rare", "Plasma", "Energy Weapons"],
    ["laser pistol", "Rare", "Energy", "Energy Weapons"],
    ["railgun", "Rare", "Kinetic", "Heavy Weapons"],
    ["enchanted longsword", "Rare", "Magic", "Melee"],
    ["dragonbone bow", "Rare", "Piercing", "Archery"],
    ["void-touched glaive", "Rare", "Void", "Polearms"],
    ["sunforged katana", "Rare", "Radiant", "Melee"],
    ["shadowstrike crossbow", "Rare", "Piercing", "Archery"],
    ["crystal spear", "Rare", "Piercing", "Polearms"],
    ["living wood bow", "Rare", "Piercing", "Archery"],
    ["gravity hammer", "Rare", "Gravity", "Melee"]
  ],
  legendary: [
    ["vorpal blade", "Legendary", "Slashing", "Melee"],
    ["holy avenger sword", "Legendary", "Radiant", "Melee"],
    ["antimatter pistol", "Legendary", "Antimatter", "Energy Weapons"],
    ["staff of fireballs", "Legendary", "Fire", "Magic"],
    ["wand of lightning storms", "Legendary", "Lightning", "Magic"],
    ["orb of frost", "Legendary", "Cold", "Magic"],
    ["reality-warping pen", "Legendary", "Reality", "Artifice"],
    ["hourglass of time", "Legendary", "Temporal", "Magic"],
    ["nanite swarm launcher", "Legendary", "Nanite", "High-Tech"],
    ["tachyon lance", "Legendary", "Temporal", "Energy Weapons"],
    ["quantum destabilizer", "Legendary", "Quantum", "High-Tech"],
    ["neural disruptor", "Legendary", "Psychic", "Psionics"],
    ["gravity whip", "Legendary", "Gravity", "Exotic"],
    ["soul-stealing scythe", "Legendary", "Necrotic", "Melee"],
    ["prismatic crystal", "Legendary", "Prismatic", "Magic"],
    ["dream catcher net", "Legendary", "Psychic", "Psionics"],
    ["mirror of trapped souls", "Legendary", "Soul", "Necromancy"],
    ["bell of mind shattering", "Legendary", "Psychic", "Psionics"],
    ["lute of sonic destruction", "Legendary", "Sonic", "Bardic"],
    ["paintbrush of creation", "Legendary", "Creation", "Artifice"]
  ]
},
prop: [
  "a locket containing a portrait of someone they can't remember",
  "a child's drawing that's their only connection to a lost family",
  "a wedding ring from a marriage that never officially ended",
  "a diary with every page blank except one mysterious entry",
  "a music box that plays a tune only they can hear the words to",
  
  "a compass that doesn't point north, but toward what you fear most",
  "a mirror that shows not your reflection, but your potential",
  "a candle that burns without consuming itself, fueled by memories",
  "a set of tarot cards that are always accurate but never clear",
  "a hourglass where the sand flows upward when important decisions approach",
  
  "a sword that whispers battle advice in a dead language",
  "a pistol with one bullet that never misses but can't be reloaded",
  "a bow made from a tree that died saving someone",
  "a dagger that grows warm in the presence of lies",
  "a staff carved from the bone of a creature that shouldn't exist",
  
  "a lockpick set that belonged to a master thief executed for their last job",
  "a surgeon's kit still stained with blood that won't wash clean",
  "a cartographer's tools that map not geography, but emotional landscapes",
  "a chef's knife that makes food taste of the cook's emotions",
  "a scribe's quill that writes in the ink color matching the writer's mood",
  
  "a book where the story changes based on who's reading it",
  "a dictionary of a language spoken only by ghosts",
  "a recipe book for meals that cure supernatural ailments",
  "a map of a city that doesn't exist (yet or anymore)",
  "a manual for a machine that was never built",
  
  "a pouch that's always slightly heavier than it should be",
  "a bottle containing a sunset from the last day of peace",
  "a box that can only be opened when no one is looking",
  "a flask that refills with whatever liquid the owner most desires",
  "a bag that always contains exactly what's needed (but never what's wanted)",
  
  "a cloak that makes the wearer forgettable to those who glance casually",
  "gloves that leave no fingerprints but always feel like they're touching something",
  "boots that remember every step taken in them",
  "a hat that grows warmer when someone is lying to you",
  "a scarf woven from the hair of everyone who ever loved the weaver",
  
  "a paintbrush that uses the artist's memories as pigment",
  "a flute that plays the songs of extinct birds",
  "a puppet that moves slightly when telling important truths",
  "a chess set where the pieces argue about strategy",
  "a sketchbook where drawings sometimes come to life temporarily",
  
  "a seed that won't grow until planted in meaningful soil",
  "a feather that floats toward important truths",
  "a stone that's warm to the touch and pulses like a heart",
  "a shell that whispers ocean secrets when held to the ear",
  "a leaf that never withers and changes color with the seasons",
  
  "a watch that tells time perfectly but for a different time zone",
  "a camera that captures what was, not what is",
  "a radio that picks up broadcasts from alternate realities",
  "a gauge that measures moral alignment rather than pressure",
  "a generator that runs on regret instead of fuel",
  
  "a preserved eye that sometimes blinks and looks around",
  "a tooth that aches when danger approaches",
  "a lock of hair that grows slowly even when detached",
  "a fingerbone on a string that points toward answers",
  "a vial of blood that boils when the donor is in mortal danger",
  
  "a jar containing a captured echo of their first laugh",
  "a bottle holding the silence from a moment of perfect peace",
  "a box with their shadow folded neatly inside",
  "a locket containing not a picture, but a scent memory",
  "a compass that points toward what you've lost, not north",
  
  "a coin that always lands on the worst possible side for the owner",
  "a doll that mimics the movements of someone somewhere",
  "a necklace that grows heavier with each lie told while wearing it",
  "a ring that can't be removed but slowly changes the wearer",
  "a book that writes itself with the reader's secrets",
  
  "a feather from an angel's wing that weighs nothing but feels heavy",
  "a fragment of a star that glows with cold, ancient light",
  "a drop of god's blood in a crystal vial that hums with power",
  "a piece of the moon that changes phases in your hand",
  "a sunbeam captured in a prism that shows possible futures",
  
  "a pocket watch that runs backward on Tuesdays",
  "a photograph that ages showing the subject's future",
  "a music box that plays the song you'll hear when you die",
  "a calendar that shows only important dates from your life",
  "a journal that writes your thoughts before you think them",
  
  "two halves of a coin, the other half held by a sworn enemy",
  "a set of matching tattoos that appear on someone else when inked",
  "a shared dream diary with someone they've never met awake",
  "a pair of glasses that lets you see through someone else's eyes",
  "a wedding band that fits perfectly but they've never been married"
],
person: [
  "an adopted sibling from a warzone who sees them as a savior",
  "a mentor figure who is secretly preparing them for sacrifice",
  "a protege who is dangerously surpassing them already",
  "a comrade-in-arms who saved their life at great personal cost",
  "a childhood friend who knows all their secrets and vulnerabilities",
  
  "a twin separated at birth, now on opposing sides of a conflict",
  "a parent who abandoned them for what they claimed was 'their own good'",
  "a sibling who resents them for being the 'chosen one'",
  "a child they didn't know existed until recently",
  "a cousin who is their only remaining family, and their polar opposite",
  
  "a former lover who is now their most dangerous enemy",
  "a soulmate they're destined to meet but haven't yet",
  "a spouse in an arranged marriage that became genuine love",
  "an unrequited love who sees them only as a friend",
  "a lover who died and was resurrected... differently",
  
  "a nemesis who is morally their opposite but intellectually their equal",
  "a former friend turned bitter rival over a fundamental disagreement",
  "someone they wronged who is now seeking justified vengeance",
  "a competitor who pushes them to be better through sheer opposition",
  "an ideological enemy they secretly respect too much",
  
  "a guardian angel/demon assigned to them who has grown too attached",
  "the person whose life they saved, who now owes them a life-debt",
  "someone who shares their dreams and memories without knowing why",
  "a ghost bound to them who offers advice (wanted or not)",
  "a parallel universe version of themselves with different life choices",
  
  "a partner in crime who knows where all the bodies are buried",
  "a commanding officer who both respects and fears their potential",
  "a student who is dangerously misinterpreting their teachings",
  "a colleague who is stealing their work and claiming it as their own",
  "a subordinate who is secretly protecting them from above",
  
  "someone they've never met but who has been writing them letters for years",
  "a person they see in every crowd but can never approach",
  "their own future self, trying to prevent/cause a specific event",
  "a sentient weapon/artifact that considers them its wielder",
  "a creature they rescued that is now imprinted on them",
  
  "the person who showed them kindness when no one else would",
  "someone they could have saved but chose not to, who survived anyway",
  "a stranger who recognized their true nature instantly",
  "the individual they were created/born to destroy/protect",
  "a mortal enemy who is, against all odds, their perfect match"
],
relStat: [
  "unbreakably bonded by shared trauma and survival",
  "mutually respectful professional partnership with deep trust",
  "familial love that transcends blood or legal definitions",
  "romantic partnership built on absolute honesty and equality",
  "mentor-student dynamic that has evolved into deep friendship",
  
  "love-hate relationship with shifting power dynamics",
  "professional rivalry with underlying mutual attraction",
  "familial obligation masking deep resentment",
  "former lovers trying to be 'just friends' and failing",
  "allies of convenience with fundamentally opposing goals",
  
  "betrayed trust that can never be fully repaired",
  "mourning what the relationship could have been",
  "held together only by mutual blackmail material",
  "one-sided devotion that the other exploits ruthlessly",
  "toxic codependency that destroys both but neither can leave",
  
  "slowly healing from past wounds with cautious optimism",
  "transitioning from one type of relationship to another",
  "rediscovering each other after years of separation",
  "testing boundaries and redefining the relationship daily",
  "growing apart despite efforts to stay connected",
  
  "publicly enemies, privately lovers/confidants",
  "relationship unknown to one or both parties (amnesia, etc.)",
  "connection known only to them, dangerous if discovered",
  "maintained through coded messages and secret meetings",
  "exists only in dreams/alternate realities",
  
  "linked by fate/destiny/prophecy against their will",
  "psychically connected, sharing thoughts and emotions",
  "bound by magical contract or life-debt",
  "reincarnated souls who keep finding each other",
  "creator-creation relationship with complex power dynamics",
  
  "relationship exists in reverse chronological order",
  "only one remembers the relationship (time travel, memory wipe)",
  "connection maintained through shared imaginary friend",
  "relationship that must be renewed/ritualized periodically",
  "exists as different types of relationships in different timelines"
],
relEff: [
  "is their sole reason for continuing to fight",
  "makes them question everything they thought they knew",
  "drives them to be a better version of themselves",
  "provides the strength they need in their darkest moments",
  "challenges their deepest beliefs and assumptions",
  
  "brings out a vulnerability they show to no one else",
  "causes them to oscillate between hope and despair",
  "makes them feel truly seen for the first time",
  "awakens emotions they didn't know they possessed",
  "creates a sense of belonging they've always craved",
  
  "causes them to act against their own self-interest",
  "makes them reckless in protection of the other",
  "brings out their most manipulative or controlling tendencies",
  "helps them overcome deep-seated fears or traumas",
  "causes them to question their own sanity at times",
  
  "tests their moral boundaries in unexpected ways",
  "forces them to confront their own capacity for good/evil",
  "becomes the standard by which they judge all other relationships",
  "challenges their fundamental understanding of right and wrong",
  "represents either their salvation or damnation, they're not sure which",
  
  "is literally changing who they are at a fundamental level",
  "has given them a purpose they lacked before",
  "makes them question whether they deserve happiness",
  "has unlocked hidden abilities or aspects of their nature",
  "feels like finding a missing piece of themselves",
  
  "creates their greatest strength and their greatest weakness",
  "puts them in constant internal conflict about their feelings",
  "makes them vulnerable in ways that scare them",
  "forces them to choose between the relationship and their goals",
  "is the source of both their best and worst decisions",
  
  "gives them something to protect beyond themselves",
  "makes them want to be worthy of the other's faith in them",
  "provides an anchor in chaotic or uncertain times",
  "represents hope in a world that offers little",
  "is the one thing they would sacrifice everything for",
  
  "slowly erodes their sense of self and autonomy",
  "brings out their most toxic or dangerous qualities",
  "makes them compromise their principles repeatedly",
  "feels like an addiction they can't break",
  "is the wound that never heals, only changes shape",
  
  "physically manifests when they're together (shared aura, etc.)",
  "alters reality slightly when they're in proximity",
  "creates time anomalies or reality distortions",
  "causes shared dreams or psychic phenomena",
  "has literal, measurable effects on their health/abilities"
],
hook: ["discover something world-changing","forced to work with untrusted person","past mistake returns","choose between right and want"]
};

const creData = {
  names: [
    "Shadowmaw", "Crystalline Wyrm", "Voidwalker", "Thornbeast", "Emberclaw", "Frostfang",
    "Spectral Stalker", "Chrono-Wyvern", "Necro-Mantis", "Psychic Leviathan", "Ironhide Behemoth",
    "Glimmerfang", "Quake-Tortoise", "Storm-Hawk", "Dream-Eater", "Soul-Weaver",
    "Rust-Drake", "Mist-Panther", "Sun-Scorpion", "Moon-Wolf", "Void-Crawler",
    "Ash-Phoenix", "Coral-Beast", "Magnet-Worm", "Glass-Serpent", "Sound-Kraken",
    "Gravity-Locust", "Data-Dragon", "Memory-Moth", "Hope-Bear", "Fear-Hound", "Draahkhar", "Gorgorrath", "Mammoth-Eater",
    "Khilgor", "Zarvarok", "Lunathar", "Vexxar", "Thalagrin", "Wraithemor", "Xalzorath",
    "Grymwaar the Gargantuan", "Dayemoon the Devourer",     
  ],
  
  sizes: [
    "minuscule (under 1 foot)",
    "tiny (1-2 feet)",
    "small (2-4 feet)", 
    "medium (4-8 feet)",
    "large (8-16 feet)",
    "huge (16-32 feet)",
    "gargantuan (32-64 feet)",
    "colossal (64+ feet)",
    "variable (changes size)",
    "dimensional (size defies measurement)"
  ],
  
  bodies: [
    "quadruped mammalian", "bipedal humanoid", "serpentine reptilian", "insectoid arthropod",
    "amorphous blob", "avian bird-like", "aquatic fish-like", "arachnid spider-like",
    "cephalopod squid-like", "fungal mushroom-like", "crystalline geometric", "energy-based formless",
    "mechanical constructed", "plant-based vegetative", "shadowy incorporeal", "gaseous vaporous",
    "liquid fluidic", "modular segmented", "symbiotic fused", "holographic projected"
  ],
  
  primaryCoverings: [
    "iridescent scales that shift color with emotion",
    "thick fur that glows in moonlight",
    "feathers that hum when agitated",
    "chitinous plates that resonate when struck",
    "rocky hide that absorbs sound",
    "crystalline exterior that refracts light",
    "shadowy substance that drinks illumination",
    "metallic skin that conducts electricity",
    "living wood that sprouts leaves seasonally",
    "translucent skin revealing internal organs",
    "bioluminescent membrane pulsing with light",
    "porous sponge-like tissue filtering nutrients from air",
    "mirrored surface reflecting surroundings",
    "prismatic hide displaying rainbows",
    "molten appearance constantly dripping",
    "frost-covered pelt perpetually cold",
    "magnetic particles aligning to fields",
    "holographic projection disguising true form",
    "circuit-like patterns glowing with energy",
    "living tapestry depicting cosmic scenes"
  ],
  
  secondaryFeatures: [
    "multiple eyes seeing different spectra",
    "horns that channel psychic energy",
    "wings that manipulate reality when flapped",
    "extra limbs for specialized tasks",
    "tentacles that taste the air",
    "bioluminescent patterns conveying emotion",
    "prehensile tail with a mind of its own",
    "antennas receiving cosmic signals",
    "frills that change color for communication",
    "spines that inject vivid hallucinations",
    "gills filtering memories from water",
    "manipulator stalks with tool-like ends",
    "sensory pits detecting dimensional tears",
    "crystalline growths storing memories",
    "symbiotic organisms living on surface",
    "portal-like openings to other places",
    "clockwork mechanisms integrated into flesh",
    "floating orbs orbiting the body",
    "vocal sacs producing harmony spheres",
    "root systems connecting to ley lines"
  ],
  
  colors: [
    "midnight black absorbing 99% of light",
    "bone white that fluoresces under UV",
    "crimson that deepens with anger",
    "emerald shifting to gold at dawn",
    "sapphire glowing from within",
    "molten gold actually liquid metal",
    "iridescent changing with viewpoint",
    "nebula-like with star points",
    "chameleon matching environment perfectly",
    "prismatic splitting light into spectra",
    "void-black where light disappears",
    "sunfire yellow burning to look at",
    "deep sea blue phosphorescent",
    "forest green with seasonal patterns",
    "volcanic orange cracking with inner heat",
    "arctic blue radiating cold",
    "quantum superposition of all colors",
    "memory-based showing past environments",
    "emotion-reactive shifting with feelings",
    "reality-glitched flickering between hues"
  ],
  
  abilities: [
    "flight via anti-gravity organs",
    "invisibility by bending light around body",
    "elemental breath weapon (choose: fire/ice/lightning/acid)",
    "telepathy across planetary distances",
    "shapeshifting into any living form observed",
    "teleportation through folded space",
    "regeneration from single cell",
    "phasing through solid matter",
    "time manipulation in local field",
    "gravity control within 100-foot radius",
    "illusion projection indistinguishable from reality",
    "dimensional storage in pocket space",
    "soul absorption and memory assimilation",
    "probability manipulation altering chance",
    "reality warping in immediate vicinity",
    "emotion harvesting to fuel abilities",
    "concept manifestation making ideas real",
    "memory implantation in other beings",
    "dream walking and manipulation",
    "song of creation/destruction"
  ],
  
  habitats: [
    "deep forests where trees whisper secrets",
    "mountain peaks touching lower atmosphere",
    "ocean depths where pressure crushes steel",
    "volcanic regions with rivers of lava",
    "tundra where cold cracks stone",
    "caves echoing with forgotten languages",
    "ruins of civilizations that knew too much",
    "cloud cities floating on thermal currents",
    "dimensional pockets with unique physics",
    "ley line intersections pulsing with power",
    "memory valleys where past events replay",
    "dreamscapes accessible only in sleep",
    "data streams of global networks",
    "shadow realms between light sources",
    "crystal deserts singing in the wind",
    "fungal forests with collective consciousness",
    "nebula nurseries birthing stars",
    "time eddies where moments repeat",
    "conceptual spaces where ideas take form",
    "the border between reality and imagination"
  ],
  
  behaviors: [
    "solitary hunter stalking unique prey",
    "pack hunter with complex social hierarchy",
    "territorial defender of sacred ground",
    "curious explorer mapping unknown realms",
    "aggressive destroyer of intruders",
    "peaceful observer recording events",
    "parasitic symbiote enhancing hosts",
    "nomadic wanderer following cosmic cycles",
    "guardian protector of ancient secrets",
    "collector hoarder of specific items",
    "artist creating ephemeral works",
    "teacher imparting dangerous knowledge",
    "trickster playing elaborate games",
    "mourner grieving lost worlds",
    "archivist preserving dying memories",
    "engineer building impossible structures",
    "composer orchestrating natural forces",
    "judge weighing souls of the dead",
    "weaver spinning fate threads",
    "dreamer shaping sleeping minds"
  ],
  
  diets: [
    "carnivorous consuming specific emotions",
    "herbivorous photosynthesizing memories",
    "feeds on magical energy from ley lines",
    "consumes emotions transforming them to light",
    "absorbs sunlight converting it to matter",
    "devours knowledge from living minds",
    "ingests time itself aging surroundings",
    "feeds on dreams leaving amnesia",
    "consumes sound creating silence pockets",
    "absorbs color leaving monochrome trails",
    "devours memories rewriting personal history",
    "feeds on probability causing unlikely events",
    "consumes shadows making areas overlit",
    "absorbs heat creating permanent cold spots",
    "devours concepts causing cognitive voids",
    "feeds on faith leaving doubt",
    "consumes hope spreading despair",
    "absorbs pain leaving numbness",
    "devours words creating aphasia",
    "feeds on beauty leaving only function"
  ],
  
  intelligences: [
    "bestial instinct-driven with single-minded purpose",
    "cunning predator understanding complex traps",
    "animal intelligence with specialized adaptation",
    "human-level cognition with unique perspective",
    "highly intelligent solving multidimensional problems",
    "cosmic wisdom understanding universal truths",
    "hive mind collective consciousness",
    "distributed intelligence across multiple bodies",
    "quantum computer-level processing",
    "emotional intelligence reading beings perfectly",
    "artificial intelligence surpassing creators",
    "eldritch knowledge incomprehensible to mortals",
    "pattern recognition seeing fate threads",
    "memory-based learning from absorbed experiences",
    "dream-logic understanding surreal connections",
    "mathematical perfection calculating all variables",
    "poetic intuition understanding metaphor literally",
    "musical intelligence hearing reality's symphony",
    "architectural vision seeing underlying structures",
    "paradoxical thinking holding contradictory truths"
  ],
  
  threats: [
    "extinct (last of its kind)",
    "rare (fewer than 10 exist)",
    "uncommon (regional populations)",
    "common (widespread but hidden)",
    "abundant (numerous across realms)",
    "mythical (existence debated)",
    "legendary (shapes cultures)",
    "forgotten (lost to history)",
    "emerging (newly evolved)",
    "vanishing (fading from reality)"
  ],
  
  dangers: [
    "harmless unless threatened",
    "minor threat to unprepared",
    "dangerous if provoked accidentally",
    "apex predator in its domain",
    "catastrophic to civilizations",
    "reality-altering by presence",
    "dimensional threat affecting multiple worlds",
    "conceptual danger erasing ideas",
    "temporal hazard disrupting time",
    "existential risk challenging reality itself"
  ],
  
  origins: [
    "natural evolution in extreme environment",
    "magical mutation from arcane disaster",
    "divine creation for specific purpose",
    "failed experiment that escaped containment",
    "extraterrestrial from dying star system",
    "dimensional traveler stranded here",
    "manifestation of collective unconscious",
    "reality glitch that gained consciousness",
    "composite of multiple fused beings",
    "artificial life that achieved sentience",
    "reincarnation of ancient force",
    "byproduct of cosmic event",
    "embodiment of abstract concept",
    "dream given physical form",
    "memory made flesh",
    "story that walked off the page",
    "mathematical formula incarnate",
    "emotion crystallized into being",
    "wish granted with unintended consequences",
    "void where something was removed"
  ],
  
  temperaments: [
    "melancholy contemplating cosmic loneliness",
    "furious at injustice across species",
    "joyful discovering new experiences",
    "terrified of its own existence",
    "curious about mortal limitations",
    "bored with eternity seeking novelty",
    "protective of what it considers beautiful",
    "vengeful for ancient wrongs",
    "playful with reality as toy",
    "philosophical questioning meaning of all",
    "parental nurturing strange offspring",
    "artistic creating transient masterpieces",
    "scholarly collecting obscure knowledge",
    "mischievous rearranging causality",
    "devotional worshipping forgotten gods",
    "entrepreneurial trading impossible goods",
    "addicted to specific sensations",
    "paranoid seeing threats everywhere",
    "optimistic despite apocalyptic evidence",
    "nihilistic enforcing meaninglessness"
  ],
  
  vulnerabilities: [
    "specific musical frequency disrupts cohesion",
    "mirrored surfaces trap reflection-soul",
    "true names spoken aloud cause paralysis",
    "certain emotions act as poison",
    "mathematical paradoxes induce seizures",
    "perfect silence causes dissipation",
    "direct sunlight petrifies over hours",
    "cold iron disrupts magical biology",
    "forgetting causes fading from existence",
    "laughter weakens reality-warping abilities",
    "certain colors induce blinding pain",
    "specific memories act as narcotic",
    "logical fallacies cause system crashes",
    "unconditional love creates dependency",
    "certain smells trigger ancestral memories",
    "being photographed steals essence",
    "hearing its own thoughts echoed back",
    "witnessing acts of pure selflessness",
    "touching living wood of world-tree",
    "consuming food prepared with kindness"
  ],
  
  myths: [
    "sang the first star into existence",
    "weaves nightmares from unwoven dreams",
    "carries the weight of forgotten promises",
    "knows where all lost things go",
    "contains library of unborn futures",
    "bears curse that created a continent",
    "is last witness to dead pantheon",
    "guards prison of impossible idea",
    "metabolizes time producing anomalies",
    "dreams the world while awake",
    "wears skin of fallen angel",
    "breathes universe into new shapes",
    "contains all colors not yet imagined",
    "remembers what reality forgot",
    "suffers for sins it didn't commit",
    "celebrates tragedies as beautiful",
    "collects last breaths of dying species",
    "is mistake even gods regret",
    "sings silence into beautiful noise",
    "is proof reality is malleable"
  ]
};

const worldData = {
  prefixes: [
    "Neo", "New", "High", "Lost", "Ancient", "Crystal", "Shadow", "Iron", "Star", "Deep",
    "Silver", "Golden", "Eternal", "Forgotten", "Mystic", "Shattered", "Dreaming", "Whispering",
    "Blood", "Bone", "Ash", "Ember", "Void", "Echo", "Silent", "Frozen", "Burning", "Drifting",
    "Spectral", "Chrono", "Quantum", "Psy", "Cyber", "Bio", "Nano", "Terra", "Aero", "Hydro",
    "Gray", "Crimson", "Obsidian", "Radiant", "Luminous", "Celestial", "Arcane", "Mythic", "Phantom", "Solar",
    "The Soulless", "Ironclad", "Storm", "Twilight", "Dawn", "Dusk", "Frost", "Gloom", "Hollow", "Lone",
    "The Glass", "The Steel", "The Silent", "The Wandering", "The Cursed", "The Blessed"
  ],
  
  suffixes: [
    "haven", "port", "city", "reach", "fall", "vale", "mark", "bridge", "spire", "home",
    "keep", "hold", "watch", "grave", "garden", "forge", "weave", "song", "dream", "veil",
    "shard", "core", "shell", "rift", "gate", "well", "root", "crown", "throne", "mirror",
    "whisper", "echo", "shadow", "light", "dawn", "dusk", "tide", "storm", "peak", "depth",
    "ridge", "field", "grove", "isle", "cliff", "bay", "harbor", "sanctum", "bastion", "citadel",
    "metropolis", "domain", "empire", "realm", "territory", "province", "sector", "quadrant", "zone", "district"
  ],
  
  worldTypes: [
    "bustling megacity that never sleeps",
    "kingdom clinging to ancient traditions",
    "metropolis of gleaming towers and dark underbellies",
    "orbital station circling a dying star",
    "floating island chain held aloft by magic",
    "underground refuge from a toxic surface",
    "desert oasis fed by underground rivers",
    "archipelago of competing city-states",
    "mountain fortress carved from living stone",
    "forest realm where trees house entire communities",
    "ocean world with floating cities on giant lily pads",
    "crystalline plane where thoughts shape reality",
    "mechanical world of gears and steam",
    "biological ecosystem that is itself sentient",
    "data realm existing only in virtual space",
    "dream dimension accessed through shared consciousness",
    "time-locked zone where past, present, and future coexist",
    "pocket universe with different physical laws",
    "ruined civilization rebuilding atop its ancestors",
    "colony ship traveling between galaxies"
  ],
  
  settings: [
    "high fantasy with magic woven into daily life",
    "cyberpunk dystopia ruled by megacorporations",
    "post-apocalyptic wasteland of surviving enclaves",
    "space opera spanning multiple star systems",
    "steampunk era of brass and invention",
    "medieval realm of knights and monsters",
    "renaissance period of art and discovery",
    "stone age struggle for survival",
    "modern day with hidden supernatural elements",
    "far future where humanity has evolved beyond recognition",
    "alternate history where key events unfolded differently",
    "mythic age where legends walk the earth",
    "prehistoric world of primordial wonders",
    "gothic horror landscape of eternal twilight",
    "sword and sorcery realm of brutal adventure",
    "science fantasy blending technology and magic",
    "utopian society hiding dark secrets",
    "dystopian regime controlling every aspect of life",
    "frontier world being colonized and explored",
    "dying planet in its final centuries"
  ],
  
  climates: [
    "temperate with gentle seasons and predictable weather",
    "tropical where humidity hangs thick in the air",
    "arctic where cold can freeze blood in seconds",
    "desert where water is more precious than gold",
    "coastal with salt spray and changing tides",
    "mountainous where thin air challenges all life",
    "magical zones where reality warps unpredictably",
    "volcanic with rivers of lava and ash-filled skies",
    "swampy where decay and growth exist in balance",
    "boreal forest of endless pine and silent snowfall",
    "savannah where predators stalk through tall grass",
    "tundra where permafrost preserves ancient secrets",
    "rainforest canopy hiding civilizations above ground",
    "subterranean world lit by bioluminescent fungi",
    "floating islands with unique microclimates",
    "storm-wracked where lightning never ceases",
    "fog-shrouded where visibility extends only feet",
    "crystal-growing where geodes form daily",
    "time-affected where seasons last for decades",
    "elemental where fire, water, earth, and air regions clash"
  ],
  
  populations: [
    "thousands clinging to survival",
    "tens of thousands forming tight communities",
    "hundreds of thousands in sprawling settlements",
    "millions in overcrowded urban centers",
    "sparse with isolated homesteads",
    "nomadic tribes following ancient routes",
    "clustered in fortress-cities",
    "distributed across orbital habitats",
    "concentrated in arcology towers",
    "living aboard generation ships",
    "existing as uploaded consciousnesses",
    "reproducing through cloning vats",
    "maintained by automated systems",
    "thriving in artificial ecosystems",
    "dwindling due to mysterious plague",
    "booming with recent discoveries",
    "stagnant for centuries",
    "cyclic with population resets",
    "artificially limited by rulers",
    "expanding into new territories",
    "segmented into factions ruled by corporate elites",
    "divided into castes based on genetic enhancements",
    "organized into guilds controlling trades and knowledge",
    "living in symbiosis with native species",
    "integrated with AI overseers managing daily life",
  ],
  
  demographics: [
    "single species dominance with suppressed minorities",
    "diverse melting pot of cultures and beings",
    "segregated by species, class, or ability",
    "human majority with uplifted animal companions",
    "non-human majority viewing humans as curiosity",
    "synthetic and organic living in uneasy peace",
    "undead and living sharing uneasy coexistence",
    "elemental beings manifesting physically",
    "psychic collective with shared consciousness",
    "shapeshifters hiding true forms",
    "energy beings in constructed bodies",
    "modular organisms that combine and separate",
    "spore-based life spreading through air",
    "crystalline entities growing slowly",
    "shadow creatures avoiding light",
    "dream-walkers who are never fully awake",
    "time-displaced from various eras",
    "dimensional refugees stranded here",
    "artificial intelligences surpassing creators",
    "gods living among mortals in disguise",
    "human majority with rare non-human beings",
    "alien majority with human minority viewed as primitive",
  ],
  
  cultures: [
    "warriors who value honor above all",
    "traders who see everything as commodity",
    "scholars preserving and expanding knowledge",
    "artists expressing reality through creation",
    "religious devotees serving higher powers",
    "technological innovators pushing boundaries",
    "nature worshippers living in harmony",
    "nomadic storytellers carrying history orally",
    "archivists cataloging every event",
    "performers where life is theater",
    "alchemists seeking transformation",
    "engineers building impossible structures",
    "dreamers shaping reality with imagination",
    "hunters tracking legendary beasts",
    "farmers cultivating unique lifeforms",
    "miners delving for cosmic resources",
    "sailors navigating strange seas",
    "aviators mastering aerial realms",
    "psychics reading and manipulating minds",
    "mages bending reality to will"
  ],
  
  governments: [
    "monarchy with divine right to rule",
    "democracy where every voice matters",
    "theocracy ruled by religious doctrine",
    "corporate board controlling all resources",
    "military junta enforcing order",
    "council of elders making decisions",
    "AI overseer optimizing society",
    "anarchy with temporary power structures",
    "feudal system of sworn loyalty",
    "meritocracy where ability determines status",
    "plutocracy where wealth equals power",
    "technocracy ruled by engineers",
    "magocracy where strongest mages lead",
    "hive mind collective consciousness",
    "dynastic families competing for control",
    "secret society pulling strings from shadows",
    "living god demanding worship",
    "machine intelligence calculating optimal paths",
    "criminal syndicate profiting from chaos",
    "philosopher kings seeking ideal society"
  ],
  
  economies: [
    "thriving trade across dimensions",
    "resource-rich but poorly distributed",
    "struggling with scarcity and rationing",
    "black market controlling essentials",
    "barter system valuing unique items",
    "post-scarcity through replication tech",
    "soul-based currency trading memories",
    "energy credits powering everything",
    "favor economy where debts are social",
    "information as most valuable commodity",
    "art and beauty as primary exchange",
    "service-based where skills are currency",
    "gift economy building community bonds",
    "predatory capitalism exploiting workers",
    "communal sharing of all resources",
    "time banking where hours are currency",
    "reputation determining access to goods",
    "cryptographic ledgers tracking all transactions",
    "biological exchange of genetic material",
    "dream-sharing as economic activity"
  ],
  
  landmarks: [
    "ancient temple to forgotten gods",
    "spire piercing the cloud layer",
    "library containing forbidden knowledge",
    "arena where fate is decided by combat",
    "palace of impossible geometry",
    "portal to other realms or times",
    "statue larger than mountains",
    "labyrinth that rewrites itself",
    "tree whose roots connect worlds",
    "forge where reality is hammered",
    "observatory watching cosmic events",
    "prison holding immortal beings",
    "academy teaching dangerous arts",
    "market trading in souls and memories",
    "hospital healing existential wounds",
    "theater where plays alter reality",
    "factory producing customized lifeforms",
    "nexus where ley lines converge",
    "archive of every life ever lived",
    "monument to collective trauma"
  ],
  
  conflicts: [
    "class warfare between haves and have-nots",
    "resource scarcity causing desperate measures",
    "religious schism over interpretation of prophecy",
    "border disputes with neighboring realms",
    "plague threatening all life",
    "succession crisis with multiple claimants",
    "cultural clash over core values",
    "technological disruption changing society",
    "magical catastrophe altering reality",
    "environmental collapse from exploitation",
    "invasion by extra-dimensional beings",
    "revolution against oppressive systems",
    "conspiracy undermining institutions",
    "prophecy of doom becoming self-fulfilling",
    "time paradox unraveling causality",
    "reality decay creating unstable zones",
    "soul drought where emotions fade",
    "memory plague erasing personal history",
    "conceptual war over defining truths",
    "existential threat to world's very being"
  ],
  
  technologies: [
    "primitive tools and simple machines",
    "medieval craftsmanship and early engineering",
    "renaissance art and scientific discovery",
    "industrial revolution with steam power",
    "modern computing and global networks",
    "advanced AI and quantum computing",
    "far future beyond current comprehension",
    "biological engineering of life itself",
    "nanotechnology manipulating matter at atomic level",
    "reality editing through mathematical principles",
    "dimensional travel between parallel worlds",
    "time manipulation devices",
    "consciousness uploading and downloading",
    "matter replication from energy",
    "psychic amplification technology",
    "magic-science fusion hybrids",
    "organic-mechanical symbiotes",
    "dream harvesting and manipulation tech",
    "emotion-powered engines",
    "concept manifestation devices"
  ],
  
  magicSystems: [
    "no magic - purely physical reality",
    "rare magic known only to initiates",
    "common magic integrated into daily life",
    "magic-tech fusion creating new possibilities",
    "reality-bending altering fundamental laws",
    "blood magic using life force as fuel",
    "rune magic inscribing power in symbols",
    "elemental magic commanding natural forces",
    "mental magic affecting thoughts and perceptions",
    "temporal magic manipulating time",
    "dimensional magic accessing other realities",
    "conceptual magic shaping ideas into reality",
    "sympathetic magic through connection and similarity",
    "true naming magic knowing essence of things",
    "dream magic shaping sleeping worlds",
    "music magic harmonizing with cosmic frequencies",
    "mathematical magic calculating perfect outcomes",
    "art magic creating through expression",
    "memory magic rewriting personal history",
    "paradox magic using contradictions as power"
  ],
  
  secrets: [
    "built on ruins of more advanced civilization",
    "forbidden knowledge that drives readers mad",
    "shadow organization controlling everything",
    "magical leyline network powering all",
    "cursed by gods for ancient transgression",
    "actually a prison for cosmic horrors",
    "dream of sleeping titan that could awaken",
    "experiment by higher-dimensional beings",
    "simulation running for unknown purpose",
    "dying slowly with no one realizing",
    "artificial construct created as test",
    "battery farm for energy-harvesting entities",
    "garden for cultivating specific emotions",
    "library containing every possible story",
    "chessboard for games between gods",
    "womb for birthing new universe",
    "crypt holding dead but dreaming gods",
    "school for ascending beings",
    "hospital for healing reality itself",
    "theater where all are actors following scripts"
  ],
  
  histories: [
    "cycle of destruction and rebirth every age",
    "golden age lost to pride and folly",
    "constant war shaping culture and technology",
    "peaceful evolution until recent discovery",
    "secret history known only to few",
    "multiple contradictory histories all true",
    "history erased by catastrophic event",
    "living history that changes with telling",
    "prophetic history predicting own future",
    "fractured history across parallel timelines",
    "dream history accessed through collective unconscious",
    "archived history in crystal records",
    "oral history preserved through song",
    "genetic history encoded in all life",
    "architectural history told through ruins",
    "mythic history blending fact and legend",
    "mathematical history as unfolding equation",
    "artistic history expressed through masterpieces",
    "emotional history of collective trauma",
    "spiritual history of souls reincarnating"
  ],
  
  futures: [
    "approaching unavoidable apocalypse",
    "on brink of utopian breakthrough",
    "stagnant for millennia to come",
    "rapidly evolving in unpredictable ways",
    "destined to repeat past mistakes",
    "fated according to ancient prophecy",
    "being written by current choices",
    "already determined by higher powers",
    "multiple possibilities coexisting",
    "unfolding in reverse chronological order",
    "dreamed into existence nightly",
    "constructed by historians looking back",
    "negotiated between conflicting factions",
    "mathematically calculated to end",
    "artistically envisioned then made real",
    "psychically projected by powerful minds",
    "genetically encoded in all beings",
    "technologically simulated for study",
    "magically woven from possibilities",
    "existentially uncertain and terrifying"
  ],
  
  aesthetics: [
    "gothic arches and eternal twilight",
    "art nouveau flowing organic forms",
    "brutalist concrete and stark angles",
    "cyberpunk neon and perpetual rain",
    "steampunk brass and steam vents",
    "bio-punk living architecture",
    "crystal geometric perfection",
    "decaying ruins overgrown with life",
    "pristine cleanliness and order",
    "chaotic collage of styles and eras",
    "minimalist simplicity and empty spaces",
    "maximalist excess and sensory overload",
    "dreamlike surreal impossible physics",
    "hyper-realistic beyond normal perception",
    "monochromatic single-color themes",
    "prismatic shifting rainbows",
    "shadowy with few light sources",
    "luminous glowing from within",
    "textural where touch defines experience",
    "sonic where sound shapes environment",
    "normal and familiar to earthly reality"
  ]
};

// ============ GENERATOR FUNCTIONS (OUTSIDE APP) ============
const randWeapon = () => {
  const r = Math.random() * 100;
  let weaponPool;
  
  if (r < 50) weaponPool = charData.weapon.common;       
  else if (r < 80) weaponPool = charData.weapon.uncommon;
  else if (r < 95) weaponPool = charData.weapon.rare;     
  else weaponPool = charData.weapon.legendary;           
  
  const weapon = weaponPool[Math.floor(Math.random() * weaponPool.length)];
  return {
    name: weapon[0],
    rarity: weapon[1],
    damage: weapon[2],
    skill: weapon[3]
  };
};

const randAge = () => {
  const ranges = [[15,80,40],[100,500,25],[1000,5000,20],[1,14,10],[10000,100000,5]];
  let r = Math.random() * 100;
  for (let [min,max,w] of ranges) {
    if (r < w) return Math.floor(Math.random() * (max-min+1)) + min;
    r -= w;
  }
  return 25;
};

const generateHook = (character, complexity = 'balanced') => {
  const { 
    name, age, species, job, goal, motiv, fear, secret, event, 
    skill, weak, person, relStat, prop, quirk, growth, miss,
    pronouns, weapon, relEff // Added 'weapon' to destructuring
  } = character;

  // Helper functions for pronoun usage
  const getPronoun = (type = 'subject') => {
    const pronoun = pronouns.toLowerCase();
    if (pronoun.includes('they')) {
      return {
        subject: 'they',
        object: 'them',
        possessive: 'their',
        reflexive: 'themselves',
        beVerb: 'are',
        hasVerb: 'have'
      };
    }
    if (pronoun.includes('she')) {
      return {
        subject: 'she',
        object: 'her',
        possessive: 'her',
        reflexive: 'herself',
        beVerb: 'is',
        hasVerb: 'has'
      };
    }
    if (pronoun.includes('he')) {
      return {
        subject: 'he',
        object: 'him',
        possessive: 'his',
        reflexive: 'himself',
        beVerb: 'is',
        hasVerb: 'has'
      };
    }
    return {
      subject: 'they',
      object: 'them',
      possessive: 'their',
      reflexive: 'themselves',
      beVerb: 'are',
      hasVerb: 'have'
    };
  };
  
  const p = getPronoun();
  
  const capFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  
  const article = (word) => {
    if (!word) return 'a';
    const firstLetter = word.charAt(0).toLowerCase();
    return ['a', 'e', 'i', 'o', 'u'].includes(firstLetter) ? 'an' : 'a';
  };
  
  const clean = (text) => {
    return text.replace(/\s+/g, ' ').trim();
  };

  const sentenceVariations = {
    startWithName: () => `${name}, ${article(species)} ${age}-year-old ${species} ${job},`,
    startWithBackground: () => `Born in ${growth},`,
    startWithEvent: () => `After ${event},`,
    startWithSecret: () => `Burdened by a terrible secret—${secret}—`,
    startWithGoal: () => `Driven by a singular purpose to ${goal},`
  };

  // Define hook templates array (only once!)
  const hookTemplates = [
    // Original shorter templates
    `${sentenceVariations.startWithName()} carries a heavy burden: ${secret}. This secret complicates ${p.possessive} quest to ${goal}, driven by ${motiv}.`,
    `${sentenceVariations.startWithEvent()} ${name} changed forever. Now, ${p.subject} seeks to ${goal} while grappling with ${p.possessive} fear of ${fear}.`,
    `${name}'s ${relStat} with ${person} defines ${p.possessive} life. ${capFirst(p.subject)} ${p.hasVerb} ${skill}, but ${p.possessive} ${weak} threatens to undermine everything.`,
    `When ${name} discovers ${prop}, ${p.subject} ${p.beVerb} drawn into a conspiracy that forces ${p.object} to confront ${secret}.`,
    `${name} remembers ${miss}, but the memory of ${event} haunts ${p.object}. Now, armed with ${p.possessive} ${skill} and ${p.possessive} weapon, ${p.subject} must ${goal}.`,
    `When ${name} discovers ${prop}, ${p.subject} ${p.beVerb} drawn into a quest to ${goal}—a journey that forces ${p.object} to confront ${p.possessive} fear of ${fear}.`,
    `With ${skill} as ${p.possessive} only advantage and ${weak} as ${p.possessive} greatest liability, ${name} must ${goal} before ${event} repeats itself.`,
    `${name}'s ${relStat} bond with ${person} ${p.beVerb} tested when ${p.subject} learns that ${p.possessive} secret—${secret}—could be the key to ${goal}.`,
    `Haunted by memories of ${event} and driven by ${motiv}, ${name} ${p.beVerb} determined to ${goal}, even if it means facing ${p.possessive} deepest fear: ${fear}.`,
    `${name} ${p.beVerb} no ordinary ${job}. With ${p.possessive} ability to ${skill} and ${p.possessive} tendency to ${quirk}, ${p.subject} may be the only one who can ${goal}.`,
    
    // NEW: More detailed templates (FIXED VERSION)
    `${sentenceVariations.startWithName()} ${p.beVerb} driven by ${motiv} to ${goal}, a mission complicated by the weight of ${secret}. Armed with ${skill} and wielding ${p.possessive} ${weapon.name}, ${p.subject} must navigate ${p.possessive} ${relStat} with ${person}, who ${relEff}. Yet ${p.possessive} ${weak} constantly threatens to unravel everything, especially when ${p.subject} remembers ${event} from ${p.possessive} past in ${growth}.`,
    
    `Haunted by memories of ${event} and burdened with ${secret}, ${name} seeks to ${goal} ${motiv}. As ${article(species)} ${species} ${job}, ${p.subject} possesses ${skill} but struggles with ${weak}. ${p.possessive} journey takes ${p.object} from ${growth} to confrontations with ${person}, with whom ${p.subject} shares ${article(relStat)} ${relStat} that ${relEff}. ${p.possessive} only comforts are ${prop} and the memory of ${miss}, though ${p.possessive} deepest fear remains ${fear}.`,
    
    `${sentenceVariations.startWithSecret()} ${name}'s life has been defined by contradiction: ${skill} yet ${weak}, ${relStat} with ${person} yet ${quirk}. Now, as ${p.subject} pursues ${goal} ${motiv}, ${p.subject} must wield ${skill} against ${p.possessive} own ${weak}. The ${weapon.name} in ${p.possessive} hand and ${prop} in ${p.possessive} pocket are poor defenses against ${fear}, especially when ${event} repeats in nightmares. Only ${person}, with whom ${p.subject} shares ${relStat}, ${relEff}.`,
    
    `From ${growth} to the present, ${name}'s path has been anything but straight. ${sentenceVariations.startWithEvent()} Then came ${secret}, a truth that reshaped everything. Now ${p.subject} ${p.beVerb} ${article(job)} with ${skill}, seeking to ${goal} ${motiv} while carrying ${prop} as a reminder of ${miss}. ${p.possessive} ${relStat} with ${person} ${relEff}, complicating every decision. With ${weapon.name} ready and ${weak} as a constant vulnerability, ${p.subject} faces the ultimate test: overcoming ${fear} to achieve what once seemed impossible.`,
    
    `${name}'s existence ${p.beVerb} a tapestry of contradictions: ${skill} woven with ${weak}, ${relStat} with ${person} interrupted by ${quirk}. ${p.possessive} origin in ${growth} and the trauma of ${event} forged someone determined to ${goal} ${motiv}, yet haunted by ${secret}. ${p.possessive} ${skill} makes ${p.object} formidable, but ${weak} makes ${p.object} fragile. The ${weapon.name} ${p.subject} carries and ${prop} ${p.subject} treasures tell only part of the story; the rest lies in ${p.possessive} ${relStat} with ${person}, which ${relEff}, and in the shadow of ${fear} that grows with each step toward ${p.possessive} destiny.`,
  ];

  // Filter by complexity if needed, but ensure we always have at least one template
  let filteredTemplates = hookTemplates;
  if (complexity === 'simple') {
    filteredTemplates = hookTemplates.filter(h => h.split(' ').length < 40);
    // If filtering removes all templates, fall back to the original
    if (filteredTemplates.length === 0) {
      filteredTemplates = hookTemplates;
    }
  } else if (complexity === 'detailed') {
    filteredTemplates = hookTemplates.filter(h => h.split(' ').length > 60);
    // If filtering removes all templates, fall back to the original
    if (filteredTemplates.length === 0) {
      filteredTemplates = hookTemplates;
    }
  }

  // Declare selectedHook only once
  let selectedHook = rand(filteredTemplates);
  
  // Add safety check
  if (!selectedHook) {
    // Fallback to first template if rand returns undefined
    selectedHook = hookTemplates[0] || "A story begins...";
  }
  
  selectedHook = clean(selectedHook);
  selectedHook = improveFlow(selectedHook);
  selectedHook = capitalizeFirst(selectedHook);
  
  if (!selectedHook.endsWith('.') && !selectedHook.endsWith('!') && !selectedHook.endsWith('?')) {
    selectedHook += '.';
  }
  
  return selectedHook;
};

const generateCreatureHook = (creature, complexity = 'balanced') => {
  const {
    name, size, body, prim, sec, col, ab1, ab2, hab, behav, diet, 
    intel, threat, danger, origin, temperament, vulnerabilities, myths
  } = creature;

  const article = (word) => {
    if (!word) return 'a';
    const firstLetter = word.charAt(0).toLowerCase();
    return ['a', 'e', 'i', 'o', 'u'].includes(firstLetter) ? 'an' : 'a';
  };

  const hookTemplates = [
    `The ${name}, ${article(size)} ${size} ${body} creature, ${hab}. With ${prim} and ${sec}, this ${threat} being ${behav}, feeding on ${diet}. ${intel}, it poses ${article(danger)} ${danger} to those who encounter it.`,
    `Legends speak of the ${name}, ${origin}. ${myths}. This ${body} entity with ${col} coloration ${behav} in ${hab}, its ${ab1} and ${ab2} making it ${article(danger)} ${danger}. ${temperament}, it ${vulnerabilities}.`,
    `Catalogued as ${name}, this ${size} ${body} exhibits remarkable ${prim}. Observed ${behav} in ${hab}, it demonstrates ${intel} and feeds exclusively on ${diet}. Its abilities include ${ab1} and ${ab2}, classified as ${danger}. Origin: ${origin}.`,
    `Hunters whisper of the ${name} in ${hab}—${article(size)} ${size} ${body} with ${sec} and ${col} skin. ${temperament} by nature, it ${behav} and hunts ${diet}. Beware its ${ab1}; many have vanished studying its ${origin}.`,
  ];

  // Filter by complexity if needed, but ensure we always have at least one template
  let filteredTemplates = hookTemplates;
  if (complexity === 'simple') {
    filteredTemplates = hookTemplates.filter(h => h.split(' ').length < 40);
    // If filtering removes all templates, fall back to the original
    if (filteredTemplates.length === 0) {
      filteredTemplates = hookTemplates;
    }
  } else if (complexity === 'detailed') {
    filteredTemplates = hookTemplates.filter(h => h.split(' ').length > 60);
    // If filtering removes all templates, fall back to the original
    if (filteredTemplates.length === 0) {
      filteredTemplates = hookTemplates;
    }
  }

  // Declare selectedHook only once
  let selectedHook = rand(filteredTemplates);
  
  // Add safety check
  if (!selectedHook) {
    // Fallback to first template if rand returns undefined
    selectedHook = hookTemplates[0] || "A mysterious creature stirs...";
  }
  
  selectedHook = selectedHook.replace(/\s+/g, ' ').trim();
  selectedHook = selectedHook.charAt(0).toUpperCase() + selectedHook.slice(1);
  if (!selectedHook.endsWith('.') && !selectedHook.endsWith('!') && !selectedHook.endsWith('?')) {
    selectedHook += '.';
  }
  
  return selectedHook;
};

const generateWorldHook = (world, complexity = 'balanced') => {
  const {
    name, type, set, clim, pop, demo, cult, gov, econ, land1, land2, 
    conf, tech, magic, sec, history, future, aesthetic
  } = world;

  const article = (word) => {
    if (!word) return 'a';
    const firstLetter = word.charAt(0).toLowerCase();
    return ['a', 'e', 'i', 'o', 'u'].includes(firstLetter) ? 'an' : 'a';
  };

  const hookTemplates = [
    `${name} is ${article(type)} ${type} in ${article(set)} ${set}, featuring ${article(aesthetic)} ${aesthetic}. Its ${clim} climate hosts ${pop}, ${demo} who practice ${cult}. Ruled by ${article(gov)} ${gov}, the economy thrives on ${econ} while ${conf} threatens stability. The ${land1} and ${land2} dominate the landscape, where ${tech} meets ${magic}. But few know ${sec}.`,
    `The history of ${name} tells of ${history}. Now, this ${type} in ${article(set)} ${set} with ${article(aesthetic)} ${aesthetic} faces ${conf} as ${pop}, ${demo} navigate ${clim} conditions under ${gov}. ${cult} defines daily life, with ${econ} driving society. The ${land1} stands testament to past glories, while the ${land2} hints at ${future}. Hidden from most: ${sec}.`,
    `${name}'s ${aesthetic} defines its character—${article(type)} ${type} in ${article(set)} ${set} where ${pop}, ${demo} live under ${clim} skies. ${cult} traditions thrive under ${gov}, with ${econ} supporting the ${land1} and ${land2}. ${conf} simmers beneath the surface, while ${tech} and ${magic} offer conflicting paths. The truth of ${sec} shapes ${future}.`,
    `${name} appears as ${article(type)} ${type} in ${article(set)} ${set}, its ${aesthetic} beauty masking deeper truths. ${pop}, ${demo} live under ${clim} conditions, practicing ${cult} under ${gov} rule. ${econ} seems prosperous around the ${land1} and ${land2}, but ${conf} brews. ${tech} and ${magic} reveal glimpses of ${sec}, connected to ${history} and threatening ${future}.`,
    `Culture defines ${name}, ${article(type)} ${type} where ${cult} shapes every aspect of life for ${pop}, ${demo}. In ${article(set)} ${set} with ${clim} environment and ${aesthetic} style, the ${gov} manages ${econ} while ${conf} simmers. The ${land1} serves as cultural heart, the ${land2} as spiritual center. ${tech} and ${magic} blend uniquely here, but ${sec} could change everything about ${future}.`,
  ];

  // Filter by complexity if needed, but ensure we always have at least one template
  let filteredTemplates = hookTemplates;
  if (complexity === 'simple') {
    filteredTemplates = hookTemplates.filter(h => h.split(' ').length < 40);
    // If filtering removes all templates, fall back to the original
    if (filteredTemplates.length === 0) {
      filteredTemplates = hookTemplates;
    }
  } else if (complexity === 'detailed') {
    filteredTemplates = hookTemplates.filter(h => h.split(' ').length > 60);
    // If filtering removes all templates, fall back to the original
    if (filteredTemplates.length === 0) {
      filteredTemplates = hookTemplates;
    }
  }

  // Declare selectedHook only once
  let selectedHook = rand(filteredTemplates);
  
  // Add safety check
  if (!selectedHook) {
    // Fallback to first template if rand returns undefined
    selectedHook = hookTemplates[0] || "A story begins...";
  }
  
  selectedHook = selectedHook.replace(/\s+/g, ' ').trim();
  selectedHook = selectedHook.charAt(0).toUpperCase() + selectedHook.slice(1);
  if (!selectedHook.endsWith('.') && !selectedHook.endsWith('!') && !selectedHook.endsWith('?')) {
    selectedHook += '.';
  }
  
  return selectedHook;
};

const genChar = (complexity = 'balanced') => {
  const species = rand(charData.species);
  const age = randAge();
  
  const character = {
    name: rand(charData.names),
    age: species === "human" ? Math.min(age, 120) : age,
    pronouns: rand(charData.pronouns),
    species: species,
    job: rand(charData.jobs),
    build: rand(charData.builds),
    skin: rand(charData.skin),
    hair: `${rand(charData.hairLen)}, ${rand(charData.hairSty)}, ${rand(charData.hairCol)}`,
    eyes: `${rand(charData.eyeCol)}, ${rand(charData.eyeDet)}`,
    cloth: rand(charData.cloth),
    dist: rand(charData.dist),
    str: rand(charData.str),
    flaw: rand(charData.flaw),
    social: rand(charData.social),
    quirk: rand(charData.quirk),
    growth: rand(charData.growth),
    event: rand(charData.event),
    miss: rand(charData.miss),
    secret: rand(charData.secret),
    goal: rand(charData.goal),
    motiv: rand(charData.motiv),
    fear: rand(charData.fear),
    skill: rand(charData.skill),
    weak: rand(charData.weak),
    weapon: randWeapon(),
    prop: rand(charData.prop),
    person: rand(charData.person),
    relStat: rand(charData.relStat),
    relEff: rand(charData.relEff),
    hook: ""
  };
  
  character.hook = generateHook(character, complexity);
  
  return character;
};

const genCre = (complexity = 'balanced') => {
  const creature = {
    name: rand(creData.names),
    size: rand(creData.sizes),
    body: rand(creData.bodies),
    prim: rand(creData.primaryCoverings),
    sec: rand(creData.secondaryFeatures),
    col: rand(creData.colors),
    ab1: rand(creData.abilities),
    ab2: rand(creData.abilities),
    hab: rand(creData.habitats),
    behav: rand(creData.behaviors),
    diet: rand(creData.diets),
    intel: rand(creData.intelligences),
    threat: rand(creData.threats),
    danger: rand(creData.dangers),
    origin: rand(creData.origins),
    temperament: rand(creData.temperaments),
    vulnerabilities: rand(creData.vulnerabilities),
    myths: rand(creData.myths),
    hook: ""
  };
  
  creature.hook = generateCreatureHook(creature, complexity);
  return creature;
};

const genWorld = (complexity = 'balanced') => {
  const world = {
    name: rand(worldData.prefixes) + rand(worldData.suffixes),
    type: rand(worldData.worldTypes),
    set: rand(worldData.settings),
    clim: rand(worldData.climates),
    pop: rand(worldData.populations),
    demo: rand(worldData.demographics),
    cult: rand(worldData.cultures),
    gov: rand(worldData.governments),
    econ: rand(worldData.economies),
    land1: rand(worldData.landmarks),
    land2: rand(worldData.landmarks),
    conf: rand(worldData.conflicts),
    tech: rand(worldData.technologies),
    magic: rand(worldData.magicSystems),
    sec: rand(worldData.secrets),
    history: rand(worldData.histories),
    future: rand(worldData.futures),
    aesthetic: rand(worldData.aesthetics),
    hook: ""
  };
  
  world.hook = generateWorldHook(world, complexity);
  return world;
};

// ============ APP COMPONENT (MAIN FUNCTION) ============
const App = () => {
  // ============ STATE VARIABLES ============
const [tab, setTab] = useState('character');
const [charHookComplexity, setCharHookComplexity] = useState('balanced');
const [creatureHookComplexity, setCreatureHookComplexity] = useState('balanced');
const [worldHookComplexity, setWorldHookComplexity] = useState('balanced');
const [c, setC] = useState(genChar('balanced'));
const [cr, setCr] = useState(genCre('balanced'));
const [w, setW] = useState(genWorld('balanced'));
const [saved, setSaved] = useState([]);
const [storyElements, setStoryElements] = useState({ character: true, creature: false, world: false });
const [story, setStory] = useState('');

  const scrollLockRef = useRef(false);
const targetPositionRef = useRef(0);

  // ============ INITIALIZATION EFFECT ============
  useEffect(() => {
    const initialChar = genChar();
    setC(initialChar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
  if (scrollLockRef.current) {
    window.scrollTo({
      top: targetPositionRef.current,
      behavior: 'auto'
    });
    scrollLockRef.current = false;
  }
});

  // ============ UTILITY FUNCTIONS (INSIDE APP) ============
  const saveCurrent = () => {
    const item = {
      id: Date.now(),
      type: tab,
      data: tab === 'character' ? c : tab === 'creature' ? cr : w,
      timestamp: new Date().toLocaleString()
    };
    setSaved([...saved, item]);
  };

  const loadSaved = (item) => {
    if (item.type === 'character') setC(item.data);
    else if (item.type === 'creature') setCr(item.data);
    else setW(item.data);
    setTab(item.type);
  };

  const deleteSaved = (id) => {
    setSaved(saved.filter(s => s.id !== id));
  };

  const exportAsText = () => {
    let text = '';
    const data = tab === 'character' ? c : tab === 'creature' ? cr : w;

    if (tab === 'character') {
      text = `CHARACTER: ${data.name}
Age: ${data.age} | Pronouns: ${data.pronouns} | Species: ${data.species}
Occupation: ${data.job}

APPEARANCE
Build: ${data.build}
Skin: ${data.skin}
Hair: ${data.hair}
Eyes: ${data.eyes}
Clothing: ${data.cloth}
Distinctive: ${data.dist}

PERSONALITY
Strength: ${data.str}
Flaw: ${data.flaw}
Social: ${data.social}
Quirk: ${data.quirk}

BACKGROUND
Grew up: ${data.growth}
Shaped by: ${data.event}
Misses: ${data.miss}
Secret: ${data.secret}

GOALS
Goal: ${data.goal}
Motivation: ${data.motiv}
Fear: ${data.fear}

SKILLS
Skill: ${data.skill}
Weakness: ${data.weak}
Weapon: ${data.weapon.name}
Weapon Rarity: ${data.weapon.rarity}
Damage Type: ${data.weapon.damage}
Required Skill: ${data.weapon.skill}
Item: ${data.prop}

RELATIONSHIPS
Person: ${data.person}
Status: ${data.relStat}
Effect: ${data.relEff}

STORY HOOK
${data.hook}`;
    } else if (tab === 'creature') {
      text = `CREATURE: ${data.name}
  
IDENTITY
Size: ${data.size}
Body Type: ${data.body}

PHYSICAL DESCRIPTION
Primary Covering: ${data.prim}
Secondary Features: ${data.sec}
Coloration: ${data.col}

ABILITIES
Primary Ability: ${data.ab1}
Secondary Ability: ${data.ab2}

NATURAL HISTORY
Habitat: ${data.hab}
Behavior: ${data.behav}
Diet: ${data.diet}
Intelligence: ${data.intel}

LORE & THREAT
Rarity: ${data.threat}
Danger Level: ${data.danger}
Origin: ${data.origin}

PERSONALITY & MYSTIQUE
Temperament: ${data.temperament}
Vulnerabilities: ${data.vulnerabilities}
Myths & Legends: ${data.myths}

STORY HOOK
${data.hook}`;
    } else if (tab === 'world') {
      text = `WORLD: ${data.name}

IDENTITY
Type: ${data.type}
Setting: ${data.set}
Aesthetic: ${data.aesthetic}

ENVIRONMENT
Climate: ${data.clim}
Primary Landmark: ${data.land1}
Secondary Landmark: ${data.land2}

SOCIETY
Population: ${data.pop}
Demographics: ${data.demo}
Culture: ${data.cult}

POWER STRUCTURES
Government: ${data.gov}
Economy: ${data.econ}
Primary Conflict: ${data.conf}

SYSTEMS & MECHANICS
Technology Level: ${data.tech}
Magic System: ${data.magic}
World Secret: ${data.sec}

HISTORY & FUTURE
Historical Pattern: ${data.history}
Projected Future: ${data.future}

STORY HOOK
${data.hook}`;
    }

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tab}-${Date.now()}.txt`;
    a.click();
  };

  const exportAsJSON = () => {
    const data = tab === 'character' ? c : tab === 'creature' ? cr : w;
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tab}-${Date.now()}.json`;
    a.click();
  };

  // ============ STORY GENERATOR FUNCTION ============
  const generateStory = () => {
    const article = (word) => {
      if (!word) return 'a';
      const firstLetter = word.charAt(0).toLowerCase();
      return ['a', 'e', 'i', 'o', 'u'].includes(firstLetter) ? 'an' : 'a';
    };
    
    const { character, creature, world } = storyElements;
    const templates = [];

    // Character only
    if (character && !creature && !world) {
      templates.push(
        `${c.name}, a ${c.age}-year-old ${c.species} ${c.job}, carries the weight of ${c.secret}. ${c.str.charAt(0).toUpperCase() + c.str.slice(1)} yet ${c.flaw}, they seek to ${c.goal} ${c.motiv}. Their ${c.weapon.name} at their side and ${c.prop} in hand, they face their greatest fear: ${c.fear}.`,
        `${c.name} has always been ${c.social}, but beneath that exterior lies someone who ${c.event}. Now, driven by ${c.motiv}, they must ${c.goal}. With ${c.skill} as their strength and ${c.weak} as their burden, the journey ahead will test everything they believe.`,
        `A ${c.species} named ${c.name} stands at a crossroads. ${c.person.charAt(0).toUpperCase() + c.person.slice(1)}, their ${c.relStat} companion, ${c.relEff}. Armed with ${c.weapon.name} and guided by ${c.str}, ${c.name} must confront what haunts them: ${c.fear}. Their quirk of ${c.quirk} is all that keeps them grounded.`
      );
    }
    
    // Creature only
    if (!character && creature && !world) {
      templates.push(
        `${cr.hook}`,
        `Encounter Report: ${cr.name}. ${cr.size} ${cr.body} sighted in ${cr.hab}. Exhibiting ${cr.behav} behavior. Primary threat: ${cr.ab1}. Secondary: ${cr.ab2}. Origin unknown, possibly ${cr.origin}. Approach with extreme caution—classified as ${cr.danger}.`,
        `The ${cr.name} is more than a monster—it's ${cr.myths}. With ${cr.prim} and ${cr.sec}, this ${cr.col} creature ${cr.behav} in ${cr.hab}, feeding on ${cr.diet}. ${cr.intel}, it represents ${cr.threat} of ${cr.danger} magnitude. ${cr.temperament}, but ${cr.vulnerabilities}.`
      );
    }

    // World only
    if (!character && !creature && world) {
      templates.push(
        `${w.hook}`,
        `Welcome to ${w.name}, where ${w.tech} meets ${w.magic}. This ${w.type} in ${article(w.set)} ${w.set} faces ${w.conf} as ${w.pop}, ${w.demo} navigate ${w.clim} conditions. The ${w.gov} maintains order while ${w.econ} drives society. But beneath the surface lies ${w.sec}, connected to ${w.history} and threatening ${w.future}.`,
        `The ${w.aesthetic} beauty of ${w.name} belies its struggles. ${w.cult} defines life in this ${w.type}, where the ${w.land1} and ${w.land2} stand as monuments to ${w.history}. With ${w.tech} advancing and ${w.magic} flowing, ${w.pop}, ${w.demo} face ${w.conf} under ${w.gov} rule. The secret? ${w.sec}. The future? ${w.future}.`
      );
    }

    // Character + Creature
    if (character && creature && !world) {
      templates.push(
        `${c.name}, the ${c.species} ${c.job}, never expected to encounter the ${cr.name}. When the ${cr.size} ${cr.body} creature emerged from ${cr.hab}, ${c.name}'s ${c.skill} was put to the ultimate test. The ${cr.col} beast's ${cr.ab1} clashed against ${c.name}'s ${c.weapon.name}, while ${c.flaw} threatened to be their undoing. This ${cr.danger} encounter would either fulfill their goal to ${c.goal}, or become their end.`,
        `The ${cr.name} had been ${cr.behav} for centuries in ${cr.hab} until ${c.name} stumbled into its territory. ${c.str.charAt(0).toUpperCase() + c.str.slice(1)} but ${c.flaw}, the ${c.age}-year-old ${c.species} realized their ${c.weapon.name} might not be enough against a creature with ${cr.ab1} and ${cr.ab2}. As ${c.name} ${c.quirk}, they remembered why they must ${c.goal}: ${c.motiv}.`,
        `${c.name}'s hunt for the legendary ${cr.name} has consumed them. This ${cr.size} creature, with its ${cr.prim} and ${cr.sec}, represents everything ${c.name} fears: ${c.fear}. Yet driven by ${c.motiv}, the ${c.social} ${c.job} tracks the beast through ${cr.hab}, knowing their ${c.weak} could prove fatal against such a ${cr.danger} foe.`
      );
    }

    // Character + World
    if (character && !creature && world) {
      templates.push(
        `${c.name} arrives in ${w.name}, a ${w.type} unlike anywhere they've seen before. The ${c.age}-year-old ${c.species} ${c.job} must navigate ${w.conf} while pursuing their goal to ${c.goal}. In a place where ${w.tech} and ${w.magic} intertwine, their ${c.skill} draws attention from the ${w.gov}. The ${w.land1} looms overhead as ${c.name} discovers ${w.sec}, a truth that changes everything.`,
        `In ${w.name}, where ${w.cult} people practice ${w.clim} survival, ${c.name} seeks answers. ${c.str.charAt(0).toUpperCase() + c.str.slice(1)} yet ${c.flaw}, they carry ${c.prop} through streets marked by ${w.conf}. The ${w.demo} population eyes the ${c.species} stranger with suspicion, unaware that ${c.name}'s ${c.secret} could either save or doom this ${w.type}.`,
        `${c.name} thought ${w.name} would be a refuge, but the ${w.type}'s ${w.econ} hides darker truths. As a ${c.social} ${c.job}, they've made allies and enemies among the ${w.pop} residents. Their ${c.person} ${c.relEff}, while ${w.conf} escalates around the ${w.land1} and ${w.land2}. ${c.name} must ${c.goal} before ${w.sec} destroys everything.`
      );
    }

    // Creature + World
    if (!character && creature && world) {
      templates.push(
        `The ${cr.name} has made ${w.name} its hunting ground. This ${cr.size} ${cr.body} creature terrorizes the ${w.type}, its ${cr.ab1} and ${cr.ab2} proving devastating against the ${w.gov}'s defenses. Citizens avoid ${cr.hab} areas near the ${w.land1}, while ${w.tech} and ${w.magic} alike fail to stop it. The ${cr.col} beast is ${cr.danger}, and ${w.conf} prevents a unified response.`,
        `In ${w.name}, a ${w.type} practicing ${w.cult} traditions, the appearance of the ${cr.name} is seen as an omen. The ${cr.size} creature with ${cr.prim} and ${cr.sec} prowls through ${w.clim} conditions, displaying ${cr.behav} behavior. Some say its ${cr.origin} is connected to ${w.sec}, while others believe it's retribution for ${w.conf}. The ${w.demo} population lives in fear.`,
        `${w.name}'s ${w.land1} and ${w.land2} now belong to the ${cr.name}. This ${cr.body} creature, classified as ${cr.danger}, has adapted to ${w.clim} conditions perfectly. The ${w.gov} struggles to protect ${w.pop} citizens as the beast demonstrates ${cr.ab1} and feeds on ${cr.diet}. Scientists debate whether its ${cr.intel} allows it to exploit ${w.conf}, or if ${w.sec} drew it here.`
      );
    }

    // All three
    if (character && creature && world) {
      templates.push(
        `${c.name}, a ${c.species} ${c.job}, arrives in ${w.name} seeking to ${c.goal}. But the ${w.type} is under siege by the ${cr.name}, a ${cr.size} ${cr.body} creature whose ${cr.ab1} has decimated the ${w.gov}'s forces. As ${w.conf} tears the ${w.pop} population apart, ${c.name} realizes their ${c.skill} and ${c.weapon.name} might be the city's only hope. With ${c.person} ${c.relEff}, they must face both the ${cr.danger} beast and ${c.fear}. The truth is, ${w.sec}, and the ${cr.name}'s ${cr.origin} may be connected.`,
        `In ${w.name}, where ${w.tech} meets ${w.magic}, ${c.name} tracks the legendary ${cr.name}. The ${c.age}-year-old ${c.species} knows this ${cr.size} creature with ${cr.prim} and ${cr.sec} holds the key to ${c.goal}. Through ${w.clim} conditions and past the ${w.land1}, they hunt their quarry. But ${c.flaw} threatens to undermine them, and ${w.conf} makes allies scarce. The ${cr.name} lurks in ${cr.hab}, its ${cr.ab2} more devastating than legends suggest.`,
        `${c.name} never wanted to be a hero, but ${w.name} needs one. The ${w.type}'s ${w.cult} people face extinction as the ${cr.name}, a ${cr.col} ${cr.body} beast, rampages through their ${w.econ}. ${c.str.charAt(0).toUpperCase() + c.str.slice(1)} yet ${c.flaw}, ${c.name} must overcome ${c.weak} to face this ${cr.danger} creature. The ${w.land1} and ${w.land2} lie in ruins, and ${w.sec} may be why the beast appeared. With ${c.prop} and ${c.weapon.name} in hand, ${c.name} makes their stand.`,
        `The ${cr.name} emerged from ${cr.hab} the same day ${c.name} entered ${w.name}. Coincidence? The ${w.gov} doesn't think so. This ${c.social} ${c.job} carries ${c.secret}, and some believe it attracted the ${cr.size} creature. As ${w.conf} escalates and the ${cr.col} beast demonstrates ${cr.ab1} and ${cr.ab2}, ${c.name} must prove they're here to ${c.goal}, not destroy. The ${w.demo} population watches warily while the ${cr.behav} creature circles closer to the ${w.land1}.`
      );
    }

    if (templates.length > 0) {
      setStory(rand(templates));
    } else {
      setStory('Please select at least one element to generate a story.');
    }
  };

  // ============ REROLL FUNCTIONS ============
  const rerollCharSection = (section) => {
  const newChar = { ...c };

  if (section === 'identity') {
    newChar.name = rand(charData.names);
    newChar.age = randAge();
    newChar.pronouns = rand(charData.pronouns);
    newChar.species = rand(charData.species);
    newChar.job = rand(charData.jobs);
  } else if (section === 'appearance') {
    newChar.build = rand(charData.builds);
    newChar.skin = rand(charData.skin);
    newChar.hair = `${rand(charData.hairLen)}, ${rand(charData.hairSty)}, ${rand(charData.hairCol)}`;
    newChar.eyes = `${rand(charData.eyeCol)}, ${rand(charData.eyeDet)}`;
    newChar.cloth = rand(charData.cloth);
    newChar.dist = rand(charData.dist);
  } else if (section === 'personality') {
    newChar.str = rand(charData.str);
    newChar.flaw = rand(charData.flaw);
    newChar.social = rand(charData.social);
    newChar.quirk = rand(charData.quirk);
  } else if (section === 'background') {
    newChar.growth = rand(charData.growth);
    newChar.event = rand(charData.event);
    newChar.miss = rand(charData.miss);
    newChar.secret = rand(charData.secret);
  } else if (section === 'goals') {
    newChar.goal = rand(charData.goal);
    newChar.motiv = rand(charData.motiv);
    newChar.fear = rand(charData.fear);
  } else if (section === 'skills') {
    newChar.skill = rand(charData.skill);
    newChar.weak = rand(charData.weak);
    newChar.weapon = randWeapon();
    newChar.prop = rand(charData.prop);
  } else if (section === 'relationships') {
    newChar.person = rand(charData.person);
    newChar.relStat = rand(charData.relStat);
    newChar.relEff = rand(charData.relEff);
  } else if (section === 'hook') {
    // Just regenerate the hook without changing traits
    newChar.hook = generateHook(newChar, charHookComplexity);
    setC(newChar);
    return; // Early return to avoid double generation
  }

  // Generate hook with complexity setting (only once!)
  newChar.hook = generateHook(newChar, charHookComplexity);
  setC(newChar);
};

  const rerollCreSection = (section) => {
  const newCre = { ...cr };

  if (section === 'identity') {
    newCre.name = rand(creData.names);
    newCre.size = rand(creData.sizes);
    newCre.body = rand(creData.bodies);
  } else if (section === 'physical') {
    newCre.prim = rand(creData.primaryCoverings);
    newCre.sec = rand(creData.secondaryFeatures);
    newCre.col = rand(creData.colors);
  } else if (section === 'abilities') {
    newCre.ab1 = rand(creData.abilities);
    newCre.ab2 = rand(creData.abilities);
  } else if (section === 'nature') {
    newCre.hab = rand(creData.habitats);
    newCre.behav = rand(creData.behaviors);
    newCre.diet = rand(creData.diets);
    newCre.intel = rand(creData.intelligences);
  } else if (section === 'lore') {
    newCre.threat = rand(creData.threats);
    newCre.danger = rand(creData.dangers);
    newCre.origin = rand(creData.origins);
  } else if (section === 'personality') {
    newCre.temperament = rand(creData.temperaments);
    newCre.vulnerabilities = rand(creData.vulnerabilities);
    newCre.myths = rand(creData.myths);
  }

  // Generate hook with complexity setting (only once!)
  newCre.hook = generateCreatureHook(newCre, creatureHookComplexity);
  setCr(newCre);
};

  const rerollWorldSection = (section) => {
  const newWorld = { ...w };

  if (section === 'identity') {
    newWorld.name = rand(worldData.prefixes) + rand(worldData.suffixes);
    newWorld.type = rand(worldData.worldTypes);
    newWorld.set = rand(worldData.settings);
  } else if (section === 'environment') {
    newWorld.clim = rand(worldData.climates);
    newWorld.land1 = rand(worldData.landmarks);
    newWorld.land2 = rand(worldData.landmarks);
  } else if (section === 'society') {
    newWorld.pop = rand(worldData.populations);
    newWorld.demo = rand(worldData.demographics);
    newWorld.cult = rand(worldData.cultures);
  } else if (section === 'power') {
    newWorld.gov = rand(worldData.governments);
    newWorld.econ = rand(worldData.economies);
    newWorld.conf = rand(worldData.conflicts);
  } else if (section === 'systems') {
    newWorld.tech = rand(worldData.technologies);
    newWorld.magic = rand(worldData.magicSystems);
    newWorld.sec = rand(worldData.secrets);
  } else if (section === 'history') {
    newWorld.history = rand(worldData.histories);
    newWorld.future = rand(worldData.futures);
    newWorld.aesthetic = rand(worldData.aesthetics);
  }

  // Generate hook with complexity setting (only once!)
  newWorld.hook = generateWorldHook(newWorld, worldHookComplexity);
  setW(newWorld);
};

  // ============ UI HELPER FUNCTIONS ============

  const Sec = ({ title, onReroll, children }) => (
  <div className="bg-white rounded-lg p-6 shadow-sm border mb-4 relative">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-bold text-gray-800">{title}</h2>
    </div>
    {children}
    <div className="mt-4 pt-4 border-t border-gray-100">
      <button 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Store current position before reroll
          const section = e.currentTarget.closest('.bg-white.rounded-lg');
          targetPositionRef.current = section?.getBoundingClientRect().top + window.scrollY || window.scrollY;
          scrollLockRef.current = true;
          
          // Call the reroll function
          onReroll();
        }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium float-right"
      >
        <RefreshCw className="w-4 h-4" /> Reroll Section
      </button>
      <div className="clear-both"></div>
    </div>
  </div>
);

    // ============ RETURN JSX ============
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-800 mb-6 text-center">WorldForge</h1>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 justify-center flex-wrap">
          {[['character',User],['creature',Ghost],['world',Globe], ['story',BookOpen]].map(([t,Icon]) => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold ${
                tab === t ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}>
              <Icon className="w-5 h-5" />
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg p-4 mb-6 shadow-sm border">
          <div className="flex gap-2 flex-wrap justify-center">
            <button onClick={saveCurrent}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Save className="w-4 h-4" />Save Current
            </button>
            <button onClick={exportAsText}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Download className="w-4 h-4" />Export as Text
            </button>
            <button onClick={exportAsJSON}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              <Download className="w-4 h-4" />Export as JSON
            </button>
          </div>
        </div>

        {/* Saved Items Section */}
        {saved.length > 0 && (
          <div className="bg-white rounded-lg p-4 mb-6 shadow-sm border">
            <h3 className="font-bold text-lg mb-3">Saved Items ({saved.length})</h3>
            <div className="space-y-2">
              {saved.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <span className="font-semibold capitalize">{item.type}</span>
                    <span className="text-gray-600 text-sm ml-2">
                      {item.type === 'character' ? item.data.name :
                       item.type === 'creature' ? item.data.name : item.data.name}
                    </span>
                    <span className="text-gray-400 text-xs ml-2">{item.timestamp}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => loadSaved(item)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm">
                      Load
                    </button>
                    <button onClick={() => deleteSaved(item.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ CHARACTER TAB ============ */}
        {tab === 'character' && (
          <>
            <div className="text-center mb-6">
              <button onClick={() => setC(genChar(charHookComplexity))}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 flex items-center gap-2 mx-auto">
                <Shuffle className="w-5 h-5" />Generate Character
              </button>
            </div>

            <Sec title="Core Identity" onReroll={() => rerollCharSection('identity')}>
              <F label="Name" value={c.name} />
              <F label="Age" value={c.age} />
              <F label="Pronouns" value={c.pronouns} />
              <F label="Species" value={c.species} />
              <F label="Occupation" value={c.job} />
            </Sec>

            <Sec title="Appearance" onReroll={() => rerollCharSection('appearance')}>
              <F label="Build" value={c.build} />
              <F label="Skin" value={c.skin} />
              <F label="Hair" value={c.hair} />
              <F label="Eyes" value={c.eyes} />
              <F label="Clothing" value={c.cloth} />
              <F label="Distinctive" value={c.dist} />
            </Sec>

            <Sec title="Personality" onReroll={() => rerollCharSection('personality')}>
              <F label="Strength" value={c.str} />
              <F label="Flaw" value={c.flaw} />
              <F label="Social" value={c.social} />
              <F label="Quirk" value={c.quirk} />
            </Sec>

            <Sec title="Background" onReroll={() => rerollCharSection('background')}>
              <F label="Grew up" value={c.growth} />
              <F label="Shaped by" value={c.event} />
              <F label="Misses" value={c.miss} />
              <F label="Secret" value={c.secret} />
            </Sec>

            <Sec title="Goals" onReroll={() => rerollCharSection('goals')}>
              <F label="Goal" value={c.goal} />
              <F label="Motivation" value={c.motiv} />
              <F label="Fear" value={c.fear} />
            </Sec>

            <Sec title="Skills" onReroll={() => rerollCharSection('skills')}>
              <F label="Skill" value={c.skill} />
              <F label="Weakness" value={c.weak} />
              <F label="Weapon" value={`${c.weapon.name}`} />
              <F label="Rarity" value={c.weapon.rarity} />
              <F label="Damage Type" value={c.weapon.damage} />
              <F label="Skill Required" value={c.weapon.skill} />
              <F label="Item" value={c.prop} />
            </Sec>

            <Sec title="Relationships" onReroll={() => rerollCharSection('relationships')}>
              <F label="Person" value={c.person} />
              <F label="Status" value={c.relStat} />
              <F label="Effect" value={c.relEff} />
            </Sec>

            <Sec title="Character Story Hook" onReroll={() => {
              const newHook = generateHook(c, charHookComplexity);
              setC({...c, hook: newHook});
            }}>
              <div className="space-y-4">
                <div className="flex gap-4 items-center">
                  <span className="text-sm text-gray-600">Hook style:</span>
                  <select 
                    value={charHookComplexity}
                    onChange={(e) => setCharHookComplexity(e.target.value)}
                    className="text-sm border rounded px-2 py-1"
                  >
                    <option value="simple">Simple</option>
                    <option value="balanced">Balanced</option>
                    <option value="detailed">Detailed</option>
                  </select>
                </div>
                
                <div className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-line min-h-[100px]">
                  {formatHookForDisplay(c.hook)}
                </div>
                
                <div className="mt-2 text-xs text-gray-500 flex gap-4">
                  <span>{calculateReadability(c.hook).words} words</span>
                  <span>{calculateReadability(c.hook).sentences} sentences</span>
                  <span>{calculateReadability(c.hook).readability} readability</span>
                </div>
              </div>
            </Sec>
          </>
        )}

        {/* ============ CREATURE TAB ============ */}
        {tab === 'creature' && (
          <>
            <div className="text-center mb-6">
              <button onClick={() => setCr(genCre(creatureHookComplexity))}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 flex items-center gap-2 mx-auto">
                <Shuffle className="w-5 h-5" />Generate Creature
              </button>
            </div>

            <Sec title="Identity" onReroll={() => rerollCreSection('identity')}>
              <F label="Name" value={cr.name} />
              <F label="Size" value={cr.size} />
              <F label="Body Type" value={cr.body} />
            </Sec>

            <Sec title="Physical Description" onReroll={() => rerollCreSection('physical')}>
              <F label="Primary Covering" value={cr.prim} />
              <F label="Secondary Features" value={cr.sec} />
              <F label="Coloration" value={cr.col} />
            </Sec>

            <Sec title="Abilities" onReroll={() => rerollCreSection('abilities')}>
              <F label="Primary Ability" value={cr.ab1} />
              <F label="Secondary Ability" value={cr.ab2} />
            </Sec>

            <Sec title="Natural History" onReroll={() => rerollCreSection('nature')}>
              <F label="Habitat" value={cr.hab} />
              <F label="Behavior" value={cr.behav} />
              <F label="Diet" value={cr.diet} />
              <F label="Intelligence" value={cr.intel} />
            </Sec>

            <Sec title="Lore & Threat" onReroll={() => rerollCreSection('lore')}>
              <F label="Rarity" value={cr.threat} />
              <F label="Danger Level" value={cr.danger} />
              <F label="Origin" value={cr.origin} />
            </Sec>

            <Sec title="Personality & Mystique" onReroll={() => rerollCreSection('personality')}>
              <F label="Temperament" value={cr.temperament} />
              <F label="Vulnerabilities" value={cr.vulnerabilities} />
              <F label="Myths & Legends" value={cr.myths} />
            </Sec>

            <Sec title="Creature Story Hook" onReroll={() => {
              const newHook = generateCreatureHook(cr, creatureHookComplexity);
              setCr({...cr, hook: newHook});
            }}>
              <div className="space-y-4">
                <div className="flex gap-4 items-center">
                  <span className="text-sm text-gray-600">Hook style:</span>
                  <select 
                    value={creatureHookComplexity}
                    onChange={(e) => setCreatureHookComplexity(e.target.value)}
                    className="text-sm border rounded px-2 py-1"
                  >
                    <option value="simple">Simple</option>
                    <option value="balanced">Balanced</option>
                    <option value="detailed">Detailed</option>
                  </select>
                </div>
                
                <div className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-line min-h-[100px]">
                  {formatHookForDisplay(cr.hook)}
                </div>
                <div className="mt-2 text-xs text-gray-500 flex gap-4">
                  <span>{calculateReadability(cr.hook).words} words</span>
                  <span>{calculateReadability(cr.hook).readability} readability</span>
                </div>
              </div>
            </Sec>
          </>
        )}

        {/* ============ WORLD TAB ============ */}
        {tab === 'world' && (
          <>
            <div className="text-center mb-6">
              <button onClick={() => setW(genWorld(worldHookComplexity))}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 flex items-center gap-2 mx-auto">
                <Shuffle className="w-5 h-5" />Generate World
              </button>
            </div>

            <Sec title="World Identity" onReroll={() => rerollWorldSection('identity')}>
              <F label="Name" value={w.name} />
              <F label="Type" value={w.type} />
              <F label="Setting" value={w.set} />
              <F label="Aesthetic" value={w.aesthetic} />
            </Sec>

            <Sec title="Environment" onReroll={() => rerollWorldSection('environment')}>
              <F label="Climate" value={w.clim} />
              <F label="Primary Landmark" value={w.land1} />
              <F label="Secondary Landmark" value={w.land2} />
            </Sec>

            <Sec title="Society" onReroll={() => rerollWorldSection('society')}>
              <F label="Population" value={w.pop} />
              <F label="Demographics" value={w.demo} />
              <F label="Culture" value={w.cult} />
            </Sec>

            <Sec title="Power Structures" onReroll={() => rerollWorldSection('power')}>
              <F label="Government" value={w.gov} />
              <F label="Economy" value={w.econ} />
              <F label="Primary Conflict" value={w.conf} />
            </Sec>

            <Sec title="Systems & Mechanics" onReroll={() => rerollWorldSection('systems')}>
              <F label="Technology Level" value={w.tech} />
              <F label="Magic System" value={w.magic} />
              <F label="World Secret" value={w.sec} />
            </Sec>

            <Sec title="History & Future" onReroll={() => rerollWorldSection('history')}>
              <F label="Historical Pattern" value={w.history} />
              <F label="Projected Future" value={w.future} />
            </Sec>

            <Sec title="World Story Hook" onReroll={() => {
              const newHook = generateWorldHook(w, worldHookComplexity);
              setW({...w, hook: newHook});
            }}>
              <div className="space-y-4">
                <div className="flex gap-4 items-center">
                  <span className="text-sm text-gray-600">Hook style:</span>
                  <select 
                    value={worldHookComplexity}
                    onChange={(e) => setWorldHookComplexity(e.target.value)}
                    className="text-sm border rounded px-2 py-1"
                  >
                    <option value="simple">Simple</option>
                    <option value="balanced">Balanced</option>
                    <option value="detailed">Detailed</option>
                  </select>
                </div>
                
                <div className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-line min-h-[100px]">
                  {formatHookForDisplay(w.hook)}
                </div>
                <div className="mt-2 text-xs text-gray-500 flex gap-4">
                  <span>{calculateReadability(w.hook).words} words</span>
                  <span>{calculateReadability(w.hook).readability} readability</span>
                </div>
              </div>
            </Sec>
          </>
        )}

        {/* ============ STORY TAB ============ */}
{tab === 'story' && (
  <div className="bg-white rounded-lg p-6 shadow-sm border">
    <h2 className="text-2xl font-bold text-gray-800 mb-6">Story Generator</h2>
    
    <div className="mb-6">
      <h3 className="font-semibold text-lg mb-3">Select elements to include:</h3>
      <div className="flex gap-6 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
          <input 
            type="checkbox" 
            checked={storyElements.character}
            onChange={() => setStoryElements({ ...storyElements, character: !storyElements.character })}
            className="w-5 h-5"
          />
          <span className="font-medium">Character</span>
          {storyElements.character && c && (
            <span className="text-sm text-gray-600 ml-2">({c.name})</span>
          )}
        </label>
        
        <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
          <input 
            type="checkbox" 
            checked={storyElements.creature}
            onChange={() => setStoryElements({ ...storyElements, creature: !storyElements.creature })}
            className="w-5 h-5"
          />
          <span className="font-medium">Creature</span>
          {storyElements.creature && cr && (
            <span className="text-sm text-gray-600 ml-2">({cr.name})</span>
          )}
        </label>
        
        <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
          <input 
            type="checkbox" 
            checked={storyElements.world}
            onChange={() => setStoryElements({ ...storyElements, world: !storyElements.world })}
            className="w-5 h-5"
          />
          <span className="font-medium">World</span>
          {storyElements.world && w && (
            <span className="text-sm text-gray-600 ml-2">({w.name})</span>
          )}
        </label>
      </div>
    </div>
    
    <button 
      onClick={generateStory}
      disabled={!storyElements.character && !storyElements.creature && !storyElements.world}
      className={`w-full py-4 rounded-lg font-semibold text-white text-lg mb-6 flex items-center justify-center gap-2
        ${!storyElements.character && !storyElements.creature && !storyElements.world
          ? 'bg-gray-400 cursor-not-allowed' 
          : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700'}`}
    >
      <BookOpen className="w-5 h-5" />
      Generate Story
    </button>
    
    {story && (
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-lg border-2 border-purple-200">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-600" />
          Your Generated Story
        </h3>
        
        <div className="bg-white p-6 rounded-lg shadow-inner mb-4">
          <div className="text-gray-700 leading-relaxed whitespace-pre-line font-serif text-[16px]">
            {story.split('. ').map((sentence, index, array) => (
              <p key={index} className="mb-3 last:mb-0">
                {sentence.trim()}{index < array.length - 1 ? '.' : ''}
              </p>
            ))}
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium">Elements used:</span>
            <div className="flex gap-1">
              {Object.entries(storyElements)
                .filter(([_, value]) => value)
                .map(([key]) => (
                  <span key={key} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium capitalize">
                    {key}
                  </span>
                ))}
            </div>
          </div>
          
          <button 
            onClick={generateStory}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <RefreshCw className="w-4 h-4" /> Regenerate
          </button>
        </div>
      </div>
    )}
    
    {!story && (storyElements.character || storyElements.creature || storyElements.world) && (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">Click "Generate Story" to create a story</p>
        <p className="text-gray-400 text-sm mt-2">using your selected elements</p>
      </div>
    )}
  </div>
)}
      </div>
    </div>
  );
};

export default App;